import { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Collapsible, CollapsibleContent } from '@/components/ui/collapsible';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CheckCircle2, Circle, ChevronDown, ChevronRight, Package, Shirt, Zap, Heart, FileText, Camera, CloudRain, Sun, Snowflake, Gem, Edit3, Plus, Save, Minus, MapPin, Share2, Shield, Landmark, Sparkles, Users } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useSubscription } from '@/hooks/useSubscription';
import { toast } from 'sonner';
import { VisaCheckModal } from './VisaCheckModal';
import { LuggageCompletionAnimation } from './LuggageCompletionAnimation';
import { SharePackingListModal } from './SharePackingListModal';
import { ShareablePackingCard } from './ShareablePackingCard';
import { TripCompletedCelebration } from './TripCompletedCelebration';
import { CollaborativePackingInvite } from './CollaborativePackingInvite';
import { useSharedPackingList, useSharedListRealtime, type SharedPackingItem } from '@/hooks/useSharedPackingList';
import { TemplateManager } from './TemplateManager';
import { savePackingListOffline, isOnline } from '@/utils/offlineStorage';

interface PackingItem {
  id: string;
  name: string;
  category: string;
  essential: boolean;
  weight?: number; // in grams
  quantity?: number;
  packed: boolean;
}

interface PackingCategory {
  id: string;
  name: string;
  icon: React.ReactNode;
  color: string;
  items: PackingItem[];
}

interface PackingListProps {
  destination: string;
  country: string;
  tripType: string;
  tripLength: number;
  startDate?: Date;
  endDate?: Date;
  luggageTypes?: string[];
  weatherData?: any; // Live weather data from PackingWorkflow
  weightUnit?: 'kg' | 'lbs';
  carryOnLimit?: number; // in kg or lbs based on weightUnit
  checkedLimit?: number; // in kg or lbs based on weightUnit
  airlineBaggagePolicy?: {
    carryOn: { weight: string; dimensions: string };
    checked: { weight: string; dimensions: string };
    personal: { weight: string; dimensions: string };
  };
  onComplete: () => void;
  onItemsChange?: (items: PackingItem[]) => void;
}

export function PackingList({ 
  destination, 
  country, 
  tripType, 
  tripLength, 
  startDate, 
  endDate, 
  luggageTypes = [],
  weatherData: liveWeatherData,
  weightUnit = 'kg',
  carryOnLimit = 0,
  checkedLimit = 0,
  airlineBaggagePolicy,
  onComplete,
  onItemsChange
}: PackingListProps) {
  const [categories, setCategories] = useState<PackingCategory[]>([]);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [isCompleted, setIsCompleted] = useState(false);
  const [userPreferences, setUserPreferences] = useState<any>(null);
  const [weatherData, setWeatherData] = useState<any>(null);
  const [editingItems, setEditingItems] = useState<Set<string>>(new Set());
  const [showAddItemDialog, setShowAddItemDialog] = useState(false);
  const [newItemName, setNewItemName] = useState('');
  const [newItemCategory, setNewItemCategory] = useState('');
  const [newItemQuantity, setNewItemQuantity] = useState(1);
  const [newItemEssential, setNewItemEssential] = useState(false);
  const [savedPreferences, setSavedPreferences] = useState<any>(null);
  const [showVisaModal, setShowVisaModal] = useState(false);
  const [showLuggageAnimation, setShowLuggageAnimation] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showShareCard, setShowShareCard] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);
  const [celebrationShown, setCelebrationShown] = useState(false);
  const [showCollaborativeInvite, setShowCollaborativeInvite] = useState(false);
  const [showTemplateManager, setShowTemplateManager] = useState(false);
  const [isOffline, setIsOffline] = useState(!isOnline());
  const [shareCode] = useState(() => Math.random().toString(36).substring(2, 10).toUpperCase());
  const hasGeneratedRef = useRef(false);
  const isMobile = useIsMobile();
  const { user } = useAuth();
  const { hasFeature } = useSubscription();
  const { ensureList, syncItems, acknowledgeRemote, listId: sharedListId } = useSharedPackingList(shareCode);

  // Flattened items used for the shared/collaborative list
  const flatSharedItems = categories.flatMap(cat =>
    cat.items.map(item => ({
      id: item.id,
      name: item.name,
      category: cat.name,
      essential: item.essential,
      packed: item.packed,
      quantity: item.quantity,
    }))
  );

  // Create the shared list row when the user opens the invite dialog
  useEffect(() => {
    if (!showCollaborativeInvite || !user) return;
    ensureList({
      destination,
      country,
      tripType,
      items: flatSharedItems,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCollaborativeInvite, user]);

  // Apply collaborators' changes to the owner's list in realtime
  const handleSharedRealtime = useCallback((remoteItems: SharedPackingItem[]) => {
    const packedById = new Map(remoteItems.map(i => [i.id, i.packed]));
    setCategories(prev => {
      let changed = false;
      const next = prev.map(category => {
        let categoryChanged = false;
        const items = category.items.map(item => {
          if (packedById.has(item.id) && packedById.get(item.id) !== item.packed) {
            categoryChanged = true;
            return { ...item, packed: packedById.get(item.id)! };
          }
          return item;
        });
        if (!categoryChanged) return category;
        changed = true;
        return { ...category, items };
      });
      // Keep the same reference when nothing actually changed so the
      // sync effect doesn't re-fire and echo the write back.
      return changed ? next : prev;
    });
    acknowledgeRemote(remoteItems);
  }, [acknowledgeRemote]);


  useSharedListRealtime(sharedListId, handleSharedRealtime);

  // Keep collaborators in sync as items change
  useEffect(() => {
    if (!user || !sharedListId) return;
    const timeout = setTimeout(() => {
      syncItems(flatSharedItems);
    }, 800);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categories, user, sharedListId]);

  // Economy tier users cannot edit packing lists
  const canEditPackingList = hasFeature('advanced_packing');



  const fetchUserPreferences = async () => {
    if (!user) return;
    
    try {
      const { data } = await supabase
        .from('user_preferences')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();
      
      setUserPreferences(data);
      
      // For first class users, also load saved packing preferences
      if (hasCustomTemplates) {
        await loadSavedPreferences();
      }
    } catch (error) {
      console.error('Error fetching user preferences:', error);
    }
  };

  const loadSavedPreferences = async () => {
    if (!user) return;
    
    try {
      // Map tripType prop to database enum values
      const dbTripType = mapTripTypeToDb(tripType);
      
      const { data } = await supabase
        .from('packing_templates')
        .select('*')
        .eq('user_id', user.id)
        .eq('trip_type', dbTripType as any)
        .eq('is_public', false)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      
      if (data) {
        setSavedPreferences(data.items);
      }
    } catch (error) {
      console.error('Error loading saved preferences:', error);
    }
  };

  const mapTripTypeToDb = (type: string) => {
    const typeMap: { [key: string]: string } = {
      'business': 'business',
      'leisure': 'leisure',
      'adventure': 'adventure',
      'family': 'family',
      'romantic': 'romantic',
      'solo': 'solo',
      'beach': 'leisure',
      'city': 'leisure',
      'cultural': 'leisure'
    };
    return typeMap[type.toLowerCase()] || 'leisure';
  };

  const savePreferences = async () => {
    if (!user || !hasFeature('custom_templates')) return;
    
    try {
      // Convert categories to items format for saving
      const itemsToSave = categories.reduce((acc, category) => {
        acc[category.id] = category.items.map(item => ({
          id: item.id,
          name: item.name,
          quantity: item.quantity || 1,
          essential: item.essential
        }));
        return acc;
      }, {} as any);
      
      const dbTripType = mapTripTypeToDb(tripType);
      
      await supabase
        .from('packing_templates')
        .upsert({
          trip_type: dbTripType as any,
          name: `${tripType} - ${tripLength} days`,
          items: itemsToSave,
          destination_type: getDestinationType(),
          is_public: false
        });
      
      toast.success('Packing preferences saved!');
    } catch (error) {
      console.error('Error saving preferences:', error);
      toast.error('Failed to save preferences');
    }
  };

  const getDestinationType = () => {
    const dest = destination.toLowerCase();
    const count = country.toLowerCase();
    
    const tropicalDestinations = ['bali', 'thailand', 'philippines', 'hawaii', 'maldives', 'caribbean'];
    const mediterraneanDestinations = ['greece', 'italy', 'spain', 'france', 'croatia', 'turkey'];
    const coldDestinations = ['iceland', 'norway', 'finland', 'alaska', 'siberia'];
    
    if (tropicalDestinations.some(d => dest.includes(d) || count.includes(d))) {
      return 'tropical';
    } else if (mediterraneanDestinations.some(d => dest.includes(d) || count.includes(d))) {
      return 'mediterranean';
    } else if (coldDestinations.some(d => dest.includes(d) || count.includes(d))) {
      return 'cold';
    }
    return 'temperate';
  };

  const isHighRiskDestination = () => {
    const count = country.toLowerCase();
    const dest = destination.toLowerCase();
    
    // Conflict zones and high-risk destinations based on current security situations
    const highRiskCountries = [
      'afghanistan', 'syria', 'yemen', 'iraq', 'somalia', 'libya',
      'south sudan', 'central african republic', 'sudan', 'mali',
      'burkina faso', 'niger', 'nigeria', 'democratic republic of congo',
      'ukraine', 'russia', 'belarus', 'north korea', 'venezuela',
      'haiti', 'eritrea', 'burundi', 'chad', 'pakistan',
      'papua new guinea', 'myanmar', 'lebanon', 'palestinian territories',
      'western sahara', 'kosovo'
    ];
    
    // High-risk regions or cities even in otherwise safe countries
    const highRiskRegions = [
      'donbas', 'crimea', 'gaza', 'west bank', 'sinai',
      'kabul', 'kandahar', 'baghdad', 'mosul', 'aleppo',
      'damascus', 'tripoli', 'benghazi', 'mogadishu', 'juba',
      'caracas', 'port-au-prince', 'n\'djamena', 'bangui'
    ];
    
    return highRiskCountries.some(c => count.includes(c)) || 
           highRiskRegions.some(r => dest.includes(r) || count.includes(r));
  };

  const getCulturalDressRequirements = () => {
    const dest = destination.toLowerCase();
    const count = country.toLowerCase();
    
    const culturalRequirements = {
      // Middle East - Conservative dress required
      conservative_middle_east: {
        destinations: [
          'saudi arabia', 'riyadh', 'jeddah', 'mecca', 'medina',
          'iran', 'tehran', 'isfahan',
          'afghanistan', 'kabul',
          'yemen', 'sanaa',
          'oman', 'muscat',
          'qatar', 'doha',
          'kuwait', 'kuwait city',
          'bahrain', 'manama'
        ],
        items: [
          { id: 'cult_me1', name: 'Long pants/trousers (covering ankles)', category: 'clothing', essential: true, weight: 400, quantity: Math.max(3, Math.ceil(tripLength / 2)), packed: false },
          { id: 'cult_me2', name: 'Long-sleeve shirts/tops', category: 'clothing', essential: true, weight: 250, quantity: Math.max(3, Math.ceil(tripLength / 2)), packed: false },
          { id: 'cult_me3', name: 'Modest long dresses/abayas', category: 'clothing', essential: true, weight: 400, quantity: Math.max(2, Math.ceil(tripLength / 3)), packed: false },
          { id: 'cult_me4', name: 'Head scarf/hijab (for women)', category: 'accessories', essential: true, weight: 80, quantity: 2, packed: false },
          { id: 'cult_me5', name: 'Light cardigan/cover-up', category: 'clothing', essential: true, weight: 300, quantity: 2, packed: false }
        ],
        note: 'Conservative dress required. Women should cover shoulders, knees, and consider head covering in certain areas.'
      },
      
      // UAE/Dubai - Moderate (more relaxed in cities, strict in religious sites)
      moderate_middle_east: {
        destinations: [
          'dubai', 'abu dhabi', 'sharjah', 'uae', 'united arab emirates'
        ],
        items: [
          { id: 'cult_uae1', name: 'Modest clothing covering shoulders and knees', category: 'clothing', essential: true, weight: 350, quantity: Math.max(2, Math.ceil(tripLength / 3)), packed: false },
          { id: 'cult_uae2', name: 'Light scarf/shawl (for mosque visits)', category: 'accessories', essential: true, weight: 100, quantity: 1, packed: false },
          { id: 'cult_uae3', name: 'Long pants or midi/maxi skirts', category: 'clothing', essential: true, weight: 350, quantity: Math.max(2, Math.ceil(tripLength / 3)), packed: false }
        ],
        note: 'Modest dress required for religious sites and government buildings. Resort areas more relaxed.'
      },
      
      // India - Modest dress for temples and religious sites
      south_asia: {
        destinations: [
          'india', 'delhi', 'mumbai', 'jaipur', 'varanasi', 'agra', 'bangalore', 'kerala',
          'nepal', 'kathmandu',
          'sri lanka', 'colombo', 'kandy',
          'bhutan', 'thimphu'
        ],
        items: [
          { id: 'cult_sa1', name: 'Modest clothing covering shoulders and knees', category: 'clothing', essential: true, weight: 300, quantity: Math.max(2, Math.ceil(tripLength / 3)), packed: false },
          { id: 'cult_sa2', name: 'Long scarves/shawls', category: 'accessories', essential: true, weight: 150, quantity: 2, packed: false },
          { id: 'cult_sa3', name: 'Long flowing pants/palazzo pants', category: 'clothing', essential: false, weight: 300, quantity: Math.max(1, Math.ceil(tripLength / 4)), packed: false },
          { id: 'cult_sa4', name: 'Long skirts/maxi dresses', category: 'clothing', essential: false, weight: 350, quantity: Math.max(1, Math.ceil(tripLength / 4)), packed: false }
        ],
        note: 'Cover shoulders and knees when visiting temples and religious sites. Remove shoes before entering.'
      },
      
      // Southeast Asia - Buddhist temples
      southeast_asia_temples: {
        destinations: [
          'thailand', 'bangkok', 'chiang mai', 'phuket',
          'cambodia', 'siem reap', 'phnom penh',
          'myanmar', 'yangon', 'bagan',
          'laos', 'luang prabang', 'vientiane',
          'vietnam', 'hanoi', 'ho chi minh', 'hoi an'
        ],
        items: [
          { id: 'cult_sea1', name: 'Clothing covering shoulders and knees (temple visits)', category: 'clothing', essential: true, weight: 300, quantity: Math.max(2, Math.ceil(tripLength / 3)), packed: false },
          { id: 'cult_sea2', name: 'Light sarong/wrap', category: 'accessories', essential: true, weight: 150, quantity: 1, packed: false },
          { id: 'cult_sea3', name: 'Lightweight long pants', category: 'clothing', essential: false, weight: 300, quantity: Math.max(1, Math.ceil(tripLength / 4)), packed: false }
        ],
        note: 'Cover shoulders and knees when visiting temples. Some temples provide sarongs to borrow.'
      },
      
      // Indonesia/Bali - Modest dress for temples
      indonesia: {
        destinations: [
          'bali', 'ubud', 'denpasar',
          'indonesia', 'jakarta', 'yogyakarta', 'java'
        ],
        items: [
          { id: 'cult_id1', name: 'Sarong (for temple visits)', category: 'accessories', essential: true, weight: 200, quantity: 1, packed: false },
          { id: 'cult_id2', name: 'Sash/temple belt', category: 'accessories', essential: false, weight: 50, quantity: 1, packed: false },
          { id: 'cult_id3', name: 'Modest tops covering shoulders', category: 'clothing', essential: true, weight: 200, quantity: Math.max(2, Math.ceil(tripLength / 3)), packed: false }
        ],
        note: 'Sarongs and sashes required for temple visits. Usually available for rent at temple entrances.'
      },
      
      // Vatican/Religious sites in Europe
      european_religious: {
        destinations: [
          'vatican', 'rome',
          'jerusalem', 'bethlehem',
          'santiago de compostela',
          'lourdes', 'fatima',
          'assisi'
        ],
        items: [
          { id: 'cult_eu1', name: 'Modest clothing covering shoulders and knees', category: 'clothing', essential: true, weight: 300, quantity: Math.max(1, Math.ceil(tripLength / 4)), packed: false },
          { id: 'cult_eu2', name: 'Shawl/light cardigan (for church visits)', category: 'accessories', essential: true, weight: 200, quantity: 1, packed: false },
          { id: 'cult_eu3', name: 'Long pants or knee-length skirt', category: 'clothing', essential: false, weight: 300, quantity: Math.max(1, Math.ceil(tripLength / 4)), packed: false }
        ],
        note: 'Cover shoulders and knees when visiting churches, cathedrals, and religious sites. No shorts or tank tops.'
      },
      
      // Japan - Formal occasions and traditional sites
      japan: {
        destinations: [
          'japan', 'tokyo', 'kyoto', 'osaka', 'nara', 'hiroshima'
        ],
        items: [
          { id: 'cult_jp1', name: 'Indoor slippers/socks', category: 'clothing', essential: true, weight: 100, quantity: 2, packed: false },
          { id: 'cult_jp2', name: 'Modest, clean clothing', category: 'clothing', essential: true, weight: 300, quantity: Math.max(2, Math.ceil(tripLength / 3)), packed: false },
          { id: 'cult_jp3', name: 'Easy-to-remove shoes', category: 'clothing', essential: true, weight: 500, quantity: 1, packed: false }
        ],
        note: 'Remove shoes when entering homes, temples, and traditional accommodations. Wear clean socks.'
      },
      
      // Orthodox Christian regions
      orthodox_christian: {
        destinations: [
          'greece', 'athens', 'santorini', 'meteora', 'mount athos',
          'russia', 'moscow', 'st petersburg',
          'armenia', 'yerevan',
          'georgia', 'tbilisi',
          'ethiopia', 'addis ababa', 'lalibela'
        ],
        items: [
          { id: 'cult_oc1', name: 'Modest clothing covering shoulders and knees', category: 'clothing', essential: true, weight: 300, quantity: Math.max(1, Math.ceil(tripLength / 4)), packed: false },
          { id: 'cult_oc2', name: 'Head scarf (for women visiting monasteries)', category: 'accessories', essential: false, weight: 50, quantity: 1, packed: false },
          { id: 'cult_oc3', name: 'Long skirt/dress (for women)', category: 'clothing', essential: false, weight: 300, quantity: 1, packed: false }
        ],
        note: 'Cover shoulders and knees when visiting churches and monasteries. Women may need head covering.'
      },
      
      // Morocco/North Africa
      north_africa: {
        destinations: [
          'morocco', 'marrakech', 'fez', 'casablanca',
          'tunisia', 'tunis',
          'algeria', 'algiers',
          'egypt', 'cairo', 'luxor', 'aswan'
        ],
        items: [
          { id: 'cult_na1', name: 'Modest clothing covering shoulders and knees', category: 'clothing', essential: true, weight: 300, quantity: Math.max(2, Math.ceil(tripLength / 3)), packed: false },
          { id: 'cult_na2', name: 'Light scarf/head covering', category: 'accessories', essential: true, weight: 80, quantity: 1, packed: false },
          { id: 'cult_na3', name: 'Loose-fitting long pants/skirts', category: 'clothing', essential: true, weight: 350, quantity: Math.max(2, Math.ceil(tripLength / 3)), packed: false }
        ],
        note: 'Modest dress appreciated, especially outside tourist areas and when visiting mosques.'
      }
    };
    
    // Check which cultural requirements apply
    const appliedRequirements: any[] = [];
    
    Object.entries(culturalRequirements).forEach(([key, requirement]) => {
      if (requirement.destinations.some(d => dest.includes(d) || count.includes(d))) {
        appliedRequirements.push(requirement);
      }
    });
    
    return appliedRequirements;
  };

  const addNewItem = () => {
    if (!newItemName.trim() || !newItemCategory) return;
    
    const newItem: PackingItem = {
      id: `custom_${Date.now()}`,
      name: newItemName.trim(),
      category: newItemCategory,
      essential: newItemEssential,
      quantity: newItemQuantity,
      packed: false
    };
    
    setCategories(prev => prev.map(category => {
      if (category.id === newItemCategory) {
        return {
          ...category,
          items: [...category.items, newItem]
        };
      }
      return category;
    }));
    
    // Reset form
    setNewItemName('');
    setNewItemCategory('');
    setNewItemQuantity(1);
    setNewItemEssential(false);
    setShowAddItemDialog(false);
    
    toast.success('Item added to packing list!');
  };

  const updateItemQuantity = (categoryId: string, itemId: string, newQuantity: number) => {
    if (newQuantity < 1) return;
    
    setCategories(prev => prev.map(category => {
      if (category.id === categoryId) {
        return {
          ...category,
          items: category.items.map(item => 
            item.id === itemId ? { ...item, quantity: newQuantity } : item
          )
        };
      }
      return category;
    }));
  };

  const removeItem = (categoryId: string, itemId: string) => {
    setCategories(prev => prev.map(category => {
      if (category.id === categoryId) {
        return {
          ...category,
          items: category.items.filter(item => item.id !== itemId)
        };
      }
      return category;
    }));
  };

  const toggleItemEdit = (itemId: string) => {
    setEditingItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  const isItemEditing = (itemId: string) => editingItems.has(itemId);

  const handleLoadTemplate = (template: any) => {
    // Load template items into categories
    const loadedCategories = categories.map(category => {
      const templateItems = template.items[category.id] || [];
      return {
        ...category,
        items: templateItems.map((item: any) => ({
          ...item,
          packed: false
        }))
      };
    });
    setCategories(loadedCategories);
    toast.success(`Template "${template.name}" loaded`);
  };

  const parseTemperature = (tempStr: string): number => {
    // Parse temperature string like "6°C" or "20°C" to number
    const match = tempStr.match(/-?\d+/);
    return match ? parseInt(match[0]) : 20; // Default to 20 if parsing fails
  };

  const analyzeActualWeather = () => {
    // Use ACTUAL weather data from live forecast if available
    if (liveWeatherData?.daily && liveWeatherData.daily.length > 0) {
      // Calculate average high and low from forecast
      const avgHigh = liveWeatherData.daily.reduce((sum: number, day: any) => 
        sum + parseTemperature(day.high), 0) / liveWeatherData.daily.length;
      const avgLow = liveWeatherData.daily.reduce((sum: number, day: any) => 
        sum + parseTemperature(day.low), 0) / liveWeatherData.daily.length;
      
      console.log(`Analyzing actual weather - Avg High: ${avgHigh}°C, Avg Low: ${avgLow}°C`);
      
      // Determine weather conditions based on ACTUAL temperatures
      const isVeryHot = avgHigh >= 30; // 30°C+
      const isHot = avgHigh >= 24 && avgHigh < 30; // 24-29°C
      const isWarm = avgHigh >= 18 && avgHigh < 24; // 18-23°C
      const isCool = avgHigh >= 10 && avgHigh < 18; // 10-17°C
      const isCold = avgHigh >= 0 && avgHigh < 10; // 0-9°C
      const isVeryCold = avgHigh < 0; // Below 0°C
      
      // Check for rain in forecast
      const hasRain = liveWeatherData.daily.some((day: any) => 
        day.condition?.toLowerCase().includes('rain') || 
        parseInt(day.precipitation) > 30
      );
      
      // Determine destination type from destination name
      const dest = destination.toLowerCase();
      const count = country.toLowerCase();
      
      const beachDestinations = ['beach', 'coast', 'island', 'bali', 'maldives', 'hawaii', 'caribbean', 'phuket'];
      const isBeach = beachDestinations.some(d => dest.includes(d) || count.includes(d));
      
      const weatherAnalysis = {
        avgHigh,
        avgLow,
        veryHot: isVeryHot,
        hot: isHot,
        warm: isWarm,
        cool: isCool,
        cold: isCold,
        veryCold: isVeryCold,
        rainy: hasRain,
        beach_suitable: isBeach && (isVeryHot || isHot || isWarm),
        sun_protection: isVeryHot || isHot || isWarm,
        mosquitos: (isVeryHot || isHot) && (isBeach || dest.includes('tropical')),
        climate: 'actual_data' // Mark that this is from real data
      };
      
      console.log('Weather analysis from actual data:', weatherAnalysis);
      setWeatherData(weatherAnalysis);
      return;
    }
    
    // Fallback to seasonal estimation if no live data
    console.log('⚠️ No live weather data available, generating fallback weather estimates for:', destination);
    generateWeatherDataFallback();
  };

  const generateWeatherDataFallback = () => {
    // Fallback: Enhanced weather data based on destination and season
    const month = startDate ? startDate.getMonth() : new Date().getMonth();
    const isSummer = month >= 5 && month <= 8; // June to September
    const isWinter = month >= 11 || month <= 2; // December to March
    const isSpring = month >= 2 && month <= 5; // March to June
    const isAutumn = month >= 8 && month <= 11; // September to December
    
    console.log('📅 Generating weather estimates for', destination, '- Month:', month, 'Season:', isSummer ? 'Summer' : isWinter ? 'Winter' : isSpring ? 'Spring' : 'Autumn');
    
    // More comprehensive destination categorization
    const dest = destination.toLowerCase();
    const count = country.toLowerCase();
    
    // Beach and tropical destinations
    const beachDestinations = [
      'bali', 'thailand', 'phuket', 'koh samui', 'philippines', 'boracay', 'palawan',
      'hawaii', 'maui', 'oahu', 'maldives', 'seychelles', 'mauritius', 'fiji',
      'caribbean', 'barbados', 'jamaica', 'bahamas', 'cancun', 'cozumel',
      'costa rica', 'tulum', 'playa del carmen', 'miami', 'key west',
      'mykonos', 'santorini', 'ibiza', 'mallorca', 'corsica', 'cyprus',
      'tel aviv', 'eilat', 'nice', 'cannes', 'monaco', 'saint-tropez',
      'goa', 'kerala', 'andaman', 'langkawi', 'penang', 'gold coast',
      'byron bay', 'bondi', 'surfers paradise'
    ];
    
    // Cold climate destinations
    const coldDestinations = [
      'iceland', 'reykjavik', 'norway', 'oslo', 'bergen', 'trondheim',
      'finland', 'helsinki', 'lapland', 'sweden', 'stockholm', 'gothenburg',
      'alaska', 'anchorage', 'fairbanks', 'siberia', 'moscow', 'st petersburg',
      'canada', 'montreal', 'quebec', 'calgary', 'vancouver',
      'patagonia', 'ushuaia', 'antarctica', 'greenland',
      'alps', 'chamonix', 'zermatt', 'innsbruck', 'aspen', 'whistler'
    ];
    
    // Mediterranean destinations
    const mediterraneanDestinations = [
      'spain', 'barcelona', 'madrid', 'seville', 'valencia',
      'italy', 'rome', 'florence', 'venice', 'naples', 'milan',
      'greece', 'athens', 'thessaloniki', 'crete', 'rhodes',
      'france', 'paris', 'lyon', 'marseille', 'bordeaux',
      'croatia', 'dubrovnik', 'split', 'zagreb',
      'portugal', 'lisbon', 'porto', 'turkey', 'istanbul', 'ankara'
    ];
    
    // Desert destinations
    const desertDestinations = [
      'dubai', 'abu dhabi', 'qatar', 'doha', 'saudi arabia', 'riyadh',
      'morocco', 'marrakech', 'casablanca', 'egypt', 'cairo', 'luxor',
      'jordan', 'amman', 'petra', 'israel', 'jerusalem',
      'arizona', 'phoenix', 'nevada', 'las vegas', 'new mexico',
      'australia', 'alice springs', 'uluru', 'ayers rock'
    ];
    
    // Tropical rainforest destinations
    const tropicalDestinations = [
      'amazon', 'brazil', 'manaus', 'colombia', 'bogota',
      'ecuador', 'quito', 'peru', 'lima', 'cusco',
      'vietnam', 'hanoi', 'ho chi minh', 'cambodia', 'siem reap',
      'laos', 'vientiane', 'malaysia', 'kuala lumpur',
      'indonesia', 'jakarta', 'yogyakarta', 'bandung'
    ];
    
    // Temperate city destinations
    const temperateDestinations = [
      'london', 'manchester', 'edinburgh', 'dublin', 'belfast',
      'berlin', 'hamburg', 'munich', 'amsterdam', 'rotterdam',
      'brussels', 'antwerp', 'zurich', 'geneva', 'bern',
      'vienna', 'salzburg', 'prague', 'budapest', 'warsaw',
      'new york', 'boston', 'chicago', 'san francisco', 'seattle',
      'toronto', 'ottawa', 'melbourne', 'sydney', 'adelaide'
    ];
    
    // Determine destination type and climate with better fallback logic
    let climate = 'temperate'; // Default fallback
    let destinationType = 'city'; // Default fallback
    
    if (beachDestinations.some(d => dest.includes(d) || count.includes(d))) {
      climate = 'tropical';
      destinationType = 'beach';
      console.log('✅ Matched as BEACH destination');
    } else if (desertDestinations.some(d => dest.includes(d) || count.includes(d))) {
      climate = 'desert';
      destinationType = 'desert';
      console.log('✅ Matched as DESERT destination');
    } else if (tropicalDestinations.some(d => dest.includes(d) || count.includes(d))) {
      climate = 'tropical';
      destinationType = 'tropical';
      console.log('✅ Matched as TROPICAL destination');
    } else if (coldDestinations.some(d => dest.includes(d) || count.includes(d))) {
      climate = 'cold';
      destinationType = 'cold';
      console.log('✅ Matched as COLD destination');
    } else if (mediterraneanDestinations.some(d => dest.includes(d) || count.includes(d))) {
      climate = 'mediterranean';
      destinationType = 'city';
      console.log('✅ Matched as MEDITERRANEAN destination');
    } else if (temperateDestinations.some(d => dest.includes(d) || count.includes(d))) {
      climate = 'temperate';
      destinationType = 'city';
      console.log('✅ Matched as TEMPERATE destination');
    } else {
      // For unknown destinations, make intelligent guesses based on country or keywords
      console.log('⚠️ Destination not in predefined lists, applying intelligent defaults for:', destination);
      
      // Check for beach/coastal keywords
      if (dest.includes('beach') || dest.includes('coast') || dest.includes('island') || dest.includes('bay') || dest.includes('shore')) {
        climate = 'tropical';
        destinationType = 'beach';
        console.log('🏖️ Detected beach keywords, using tropical beach climate');
      }
      // Check for mountain/cold keywords
      else if (dest.includes('mountain') || dest.includes('alps') || dest.includes('ski') || dest.includes('snow')) {
        climate = 'cold';
        destinationType = 'cold';
        console.log('🏔️ Detected mountain/cold keywords, using cold climate');
      }
      // Default to seasonal temperate (safe fallback that adjusts with season)
      else {
        climate = 'temperate';
        destinationType = 'city';
        console.log('🌍 Using default temperate climate with seasonal adjustments');
      }
    }
    
    // Enhanced weather patterns
    const weatherPatterns: { [key: string]: any } = {
      'tropical': {
        hot: true,
        humid: true,
        rainy: destinationType === 'tropical' ? isSummer : false,
        mosquitos: true,
        sun_protection: true,
        beach_suitable: destinationType === 'beach'
      },
      'desert': {
        hot: true,
        dry: true,
        sun_protection: true,
        temperature_variation: true, // Hot days, cool nights
        rainy: false,
        beach_suitable: false
      },
      'mediterranean': {
        hot: isSummer,
        mild: isSpring || isAutumn,
        cool: isWinter,
        rainy: isWinter || isAutumn,
        sun_protection: isSummer,
        beach_suitable: destinationType === 'beach' && (isSummer || isSpring)
      },
      'temperate': {
        hot: isSummer,
        mild: isSpring || isAutumn,
        cool: isWinter,
        rainy: true,
        sun_protection: isSummer,
        beach_suitable: destinationType === 'beach' && isSummer
      },
      'cold': {
        cold: true,
        veryCold: isWinter,
        rainy: !isWinter,
        snow: isWinter,
        sun_protection: false,
        beach_suitable: false
      }
    };
    
    const weatherData = weatherPatterns[climate] || weatherPatterns['temperate'];
    weatherData.destinationType = destinationType;
    weatherData.climate = climate;
    
    console.log('🌤️ Final weather data:', {
      climate,
      destinationType,
      hot: weatherData.hot,
      cold: weatherData.cold,
      rainy: weatherData.rainy,
      beach_suitable: weatherData.beach_suitable
    });
    
    setWeatherData(weatherData);
  };

  // Check offline mode feature once
  const hasOfflineMode = hasFeature('offline_mode');
  const hasCustomTemplates = hasFeature('custom_templates');

  useEffect(() => {
    fetchUserPreferences();
    analyzeActualWeather();

    // Monitor online/offline status for First Class users
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);
    
    if (hasOfflineMode) {
      window.addEventListener('online', handleOnline);
      window.addEventListener('offline', handleOffline);
      return () => {
        window.removeEventListener('online', handleOnline);
        window.removeEventListener('offline', handleOffline);
      };
    }
  }, [user, destination, country, startDate, liveWeatherData, hasOfflineMode]);

  useEffect(() => {
    // Generate packing list only when key dependencies change
    // Reset the generated flag when dependencies change to allow regeneration
    hasGeneratedRef.current = false;
  }, [destination, tripType, tripLength, luggageTypes.join(','), carryOnLimit, checkedLimit, weightUnit]);

  useEffect(() => {
    // Generate once we have all the data we need
    if (!hasGeneratedRef.current && (userPreferences !== null || weatherData !== null)) {
      console.log('🔄 Generating packing list with loaded data');
      generatePackingList();
      hasGeneratedRef.current = true;
    }
  }, [userPreferences, weatherData]);

  // Auto-save to offline storage for First Class users
  useEffect(() => {
    if (hasOfflineMode && categories.length > 0) {
      const tripId = `${destination}_${tripType}_${Date.now()}`;
      savePackingListOffline(tripId, destination, country, tripType, categories);
    }
  }, [categories, hasOfflineMode, destination, country, tripType]);

  const generatePackingList = () => {
    console.log('📦 Generating packing list for:', {
      destination,
      tripType,
      tripLength,
      hasWeatherData: !!weatherData,
      weatherClimate: weatherData?.climate,
      weatherDestType: weatherData?.destinationType
    });
    
    // Define effectiveWeather at the top so it's available throughout the function
    const effectiveWeather = weatherData || {
      warm: true,
      mild: true,
      sun_protection: false,
      rainy: false,
      beach_suitable: false,
      climate: 'temperate',
      destinationType: 'city'
    };
    
    console.log('🌡️ Using weather data for clothing:', effectiveWeather);
    
    // Helper function to calculate trip-type adjusted quantities
    const getClothingQuantity = (baseFormula: number, tripTypeMultiplier: number = 1) => {
      return Math.max(1, Math.ceil(baseFormula * tripTypeMultiplier));
    };
    
    // Adjust base quantities based on trip type
    const tripTypeAdjustments = {
      business: { casual: 0.5, formal: 1.5, basic: 1 },
      adventure: { casual: 1.2, formal: 0.3, basic: 1.2 },
      beach: { casual: 1.3, formal: 0.3, basic: 1 },
      romantic: { casual: 0.8, formal: 1.2, basic: 1 },
      family: { casual: 1.1, formal: 0.5, basic: 1.2 },
      solo: { casual: 1, formal: 0.7, basic: 1 },
      leisure: { casual: 1, formal: 0.5, basic: 1 },
    };
    
    const currentTripType = tripType.toLowerCase() as keyof typeof tripTypeAdjustments;
    const adjustments = tripTypeAdjustments[currentTripType] || { casual: 1, formal: 1, basic: 1 };
    
    const baseItems: { [key: string]: PackingItem[] } = {
      clothing: [
        { id: 'c1', name: 'Underwear', category: 'clothing', essential: true, weight: 30, quantity: Math.max(tripLength + 1, Math.ceil((tripLength + 2) * adjustments.basic)), packed: false },
        { id: 'c2', name: 'Socks', category: 'clothing', essential: true, weight: 50, quantity: Math.max(tripLength, Math.ceil((tripLength + 1) * adjustments.basic)), packed: false },
        { id: 'c3', name: 'T-shirts', category: 'clothing', essential: tripType.toLowerCase() !== 'business', weight: 200, quantity: getClothingQuantity(Math.max(3, tripLength / 2), adjustments.casual), packed: false },
        { id: 'c4', name: 'Pants/Jeans', category: 'clothing', essential: tripType.toLowerCase() !== 'business', weight: 450, quantity: getClothingQuantity(Math.max(2, tripLength / 4), adjustments.casual), packed: false },
        { id: 'c9', name: 'Sleepwear/Pajamas', category: 'clothing', essential: true, weight: 150, quantity: Math.max(2, Math.ceil(tripLength / 5)), packed: false },
      ],
      toiletries: [
        { id: 't1', name: 'Toothbrush', category: 'toiletries', essential: true, weight: 20, quantity: 1, packed: false },
        { id: 't2', name: 'Toothpaste', category: 'toiletries', essential: true, weight: 100, quantity: 1, packed: false },
        { id: 't3', name: 'Mouthwash', category: 'toiletries', essential: false, weight: 150, quantity: 1, packed: false },
        { id: 't4', name: 'Dental floss', category: 'toiletries', essential: false, weight: 10, quantity: 1, packed: false },
        { id: 't5', name: 'Shampoo', category: 'toiletries', essential: true, weight: 200, quantity: Math.max(1, Math.ceil(tripLength / 7)), packed: false },
        { id: 't6', name: 'Body wash/Soap', category: 'toiletries', essential: true, weight: 150, quantity: Math.max(1, Math.ceil(tripLength / 7)), packed: false },
        { id: 't7', name: 'Deodorant', category: 'toiletries', essential: true, weight: 80, quantity: 1, packed: false },
        { id: 't8', name: 'Comb/Hairbrush', category: 'toiletries', essential: true, weight: 60, quantity: 1, packed: false },
        { id: 't9', name: 'Hair cream or gel', category: 'toiletries', essential: true, weight: 100, quantity: 1, packed: false },
        { id: 't10', name: 'Body lotion', category: 'toiletries', essential: false, weight: 120, quantity: 1, packed: false },
        { id: 't11', name: 'Face cream', category: 'toiletries', essential: false, weight: 80, quantity: 1, packed: false },
        { id: 't12', name: 'Moisturizer', category: 'toiletries', essential: false, weight: 100, quantity: 1, packed: false },
        { id: 't13', name: 'Lip balm', category: 'toiletries', essential: false, weight: 15, quantity: 1, packed: false },
        { id: 't14', name: 'Hand cream', category: 'toiletries', essential: false, weight: 50, quantity: 1, packed: false },
        { id: 't15', name: 'Cotton pads', category: 'toiletries', essential: false, weight: 40, quantity: Math.max(1, Math.ceil(tripLength / 5)), packed: false },
        { id: 't16', name: 'Nail file', category: 'toiletries', essential: false, weight: 20, quantity: 1, packed: false },
        { id: 't17', name: 'Tweezers', category: 'toiletries', essential: false, weight: 25, quantity: 1, packed: false },
        { id: 't18', name: 'Perfume', category: 'toiletries', essential: true, weight: 100, quantity: 1, packed: false },
        { id: 't19', name: 'Sunscreen', category: 'toiletries', essential: true, weight: 120, quantity: Math.max(1, Math.ceil(tripLength / 7)), packed: false },
        { id: 't20', name: 'Medications', category: 'toiletries', essential: true, weight: 50, quantity: 1, packed: false },
        { id: 't21', name: 'Contact lenses/Glasses', category: 'toiletries', essential: false, weight: 30, quantity: 1, packed: false },
        { id: 't22', name: 'Razor/Shaving supplies', category: 'toiletries', essential: false, weight: 60, quantity: 1, packed: false },
      ],
      electronics: [
        { id: 'e1', name: 'Phone charger', category: 'electronics', essential: true, weight: 100, quantity: 1, packed: false },
        { id: 'e2', name: 'Phone', category: 'electronics', essential: true, weight: 200, quantity: 1, packed: false },
        { id: 'e3', name: 'Power bank', category: 'electronics', essential: false, weight: 300, quantity: 1, packed: false },
        { id: 'e4', name: 'Camera', category: 'electronics', essential: false, weight: 500, quantity: 1, packed: false },
        { id: 'e5', name: 'Camera charger', category: 'electronics', essential: false, weight: 80, quantity: 1, packed: false },
        { id: 'e6', name: 'Laptop', category: 'electronics', essential: false, weight: 1500, quantity: 1, packed: false },
        { id: 'e7', name: 'Laptop charger', category: 'electronics', essential: false, weight: 200, quantity: 1, packed: false },
        { id: 'e8', name: 'Headphones', category: 'electronics', essential: false, weight: 150, quantity: 1, packed: false },
        { id: 'e9', name: 'Earphones/AirPods', category: 'electronics', essential: false, weight: 50, quantity: 1, packed: false },
        { id: 'e10', name: 'Universal adapter', category: 'electronics', essential: false, weight: 100, quantity: 1, packed: false },
        { id: 'e11', name: 'AirTag', category: 'electronics', essential: false, weight: 11, quantity: 2, packed: false },
      ],
      documents: [
        { id: 'd1', name: 'Passport', category: 'documents', essential: true, weight: 50, quantity: 1, packed: false },
        { id: 'd2', name: 'Travel insurance', category: 'documents', essential: true, weight: 10, quantity: 1, packed: false },
        { id: 'd3', name: 'Flight tickets', category: 'documents', essential: true, weight: 10, quantity: 1, packed: false },
        { id: 'd4', name: 'Train/Bus tickets', category: 'documents', essential: false, weight: 10, quantity: 1, packed: false },
        { id: 'd5', name: 'Hotel confirmations', category: 'documents', essential: true, weight: 10, quantity: 1, packed: false },
        { id: 'd6', name: 'Driver\'s license', category: 'documents', essential: false, weight: 5, quantity: 1, packed: false },
        { id: 'd7', name: 'Credit cards', category: 'documents', essential: true, weight: 10, quantity: 2, packed: false },
        { id: 'd8', name: 'Debit cards', category: 'documents', essential: true, weight: 5, quantity: 1, packed: false },
        { id: 'd9', name: 'Cash', category: 'documents', essential: true, weight: 5, quantity: 1, packed: false },
        { id: 'd10', name: 'Emergency contacts', category: 'documents', essential: true, weight: 5, quantity: 1, packed: false },
        { id: 'd11', name: 'Visa (if required)', category: 'documents', essential: false, weight: 5, quantity: 1, packed: false },
      ],
      accessories: [
        { id: 'ac1', name: 'Hat/Cap', category: 'accessories', essential: false, weight: 80, quantity: 1, packed: false },
        { id: 'ac2', name: 'Belt', category: 'accessories', essential: false, weight: 120, quantity: 1, packed: false },
        { id: 'ac5', name: 'Laundry bag', category: 'accessories', essential: false, weight: 50, quantity: 1, packed: false },
        { id: 'ac6', name: 'Jewelry', category: 'accessories', essential: false, weight: 50, quantity: 1, packed: false },
        { id: 'ac7', name: 'Watch', category: 'accessories', essential: false, weight: 100, quantity: 1, packed: false },
        { id: 'ac8', name: 'Sunglasses', category: 'accessories', essential: false, weight: 50, quantity: 1, packed: false },
      ],
      misc: [
        { id: 'm1', name: 'Night bag/Day bag', category: 'misc', essential: true, weight: 400, quantity: 1, packed: false },
        { id: 'm2', name: 'Water bottle', category: 'misc', essential: false, weight: 200, quantity: 1, packed: false },
        { id: 'm3', name: 'Snacks', category: 'misc', essential: false, weight: 150, quantity: Math.max(2, Math.ceil(tripLength / 3)), packed: false },
        { id: 'm4', name: 'Entertainment (books, etc.)', category: 'misc', essential: false, weight: 300, quantity: Math.max(1, Math.ceil(tripLength / 7)), packed: false },
        { id: 'm5', name: 'Travel pillow', category: 'misc', essential: false, weight: 200, quantity: 1, packed: false },
        { id: 'm6', name: 'Eye mask', category: 'misc', essential: false, weight: 20, quantity: 1, packed: false },
        { id: 'm7', name: 'Earplugs', category: 'misc', essential: false, weight: 5, quantity: Math.max(2, Math.ceil(tripLength / 4)), packed: false },
      ],
      safety: [],
      cultural: []
    };

    // Final safety check: ensure all base items are valid objects with a name
    Object.keys(baseItems).forEach((key) => {
      baseItems[key] = (baseItems[key] || []).filter(
        (item) => item && typeof item.name === 'string' && item.name.trim().length > 0
      );
    });
 
    // Add safety equipment for high-risk destinations
    if (isHighRiskDestination()) {
      baseItems.safety = [
        { id: 's1', name: 'First aid kit (comprehensive)', category: 'safety', essential: true, weight: 500, quantity: 1, packed: false },
        { id: 's2', name: 'Emergency whistle', category: 'safety', essential: true, weight: 20, quantity: 1, packed: false },
        { id: 's3', name: 'Flashlight with extra batteries', category: 'safety', essential: true, weight: 200, quantity: 1, packed: false },
        { id: 's4', name: 'Water purification tablets', category: 'safety', essential: true, weight: 50, quantity: 1, packed: false },
        { id: 's5', name: 'Emergency blanket (thermal)', category: 'safety', essential: true, weight: 80, quantity: 1, packed: false },
        { id: 's6', name: 'Multi-tool/Swiss Army knife', category: 'safety', essential: true, weight: 150, quantity: 1, packed: false },
        { id: 's7', name: 'Satellite phone/GPS tracker', category: 'safety', essential: true, weight: 300, quantity: 1, packed: false },
        { id: 's8', name: 'Personal safety alarm', category: 'safety', essential: true, weight: 40, quantity: 1, packed: false },
        { id: 's9', name: 'Emergency cash (USD/EUR)', category: 'safety', essential: true, weight: 5, quantity: 1, packed: false },
        { id: 's10', name: 'Photocopies of important documents', category: 'safety', essential: true, weight: 20, quantity: 2, packed: false },
        { id: 's11', name: 'Emergency contact cards', category: 'safety', essential: true, weight: 5, quantity: 5, packed: false },
        { id: 's12', name: 'Portable door lock/security wedge', category: 'safety', essential: false, weight: 100, quantity: 1, packed: false },
        { id: 's13', name: 'Anti-theft backpack/bag', category: 'safety', essential: true, weight: 400, quantity: 1, packed: false },
        { id: 's14', name: 'Travel insurance documents (hardcopy)', category: 'safety', essential: true, weight: 10, quantity: 1, packed: false },
        { id: 's15', name: 'Local embassy contact info', category: 'safety', essential: true, weight: 5, quantity: 1, packed: false },
        { id: 's16', name: 'Mosquito repellent (strong formula)', category: 'safety', essential: true, weight: 100, quantity: 1, packed: false },
        { id: 's17', name: 'Antimalarial medication', category: 'safety', essential: true, weight: 30, quantity: 1, packed: false },
        { id: 's18', name: 'Antibiotics (prescribed)', category: 'safety', essential: false, weight: 20, quantity: 1, packed: false },
        { id: 's19', name: 'Rehydration salts', category: 'safety', essential: true, weight: 50, quantity: 5, packed: false },
        { id: 's20', name: 'N95 masks/respirators', category: 'safety', essential: true, weight: 30, quantity: 10, packed: false },
        { id: 's21', name: 'Hand sanitizer (travel size)', category: 'safety', essential: true, weight: 80, quantity: 2, packed: false },
        { id: 's22', name: 'Duct tape (small roll)', category: 'safety', essential: false, weight: 100, quantity: 1, packed: false },
        { id: 's23', name: 'Paracord/strong rope', category: 'safety', essential: false, weight: 150, quantity: 1, packed: false },
        { id: 's24', name: 'Local SIM card with data', category: 'safety', essential: true, weight: 5, quantity: 1, packed: false },
        { id: 's25', name: 'Portable solar charger', category: 'safety', essential: false, weight: 300, quantity: 1, packed: false },
      ];
    }

    // Apply saved preferences for first class users
    if (hasFeature('custom_templates') && savedPreferences) {
      Object.keys(savedPreferences).forEach(categoryKey => {
        if (baseItems[categoryKey]) {
          const savedItems = savedPreferences[categoryKey];
          baseItems[categoryKey] = baseItems[categoryKey].map(item => {
            const savedItem = savedItems.find((s: any) => s.id === item.id);
            if (savedItem) {
              return {
                ...item,
                quantity: savedItem.quantity,
                essential: savedItem.essential
              };
            }
            return item;
          });
          
          // Add any custom items from saved preferences
          savedItems.forEach((savedItem: any) => {
            if (!baseItems[categoryKey].find(item => item.id === savedItem.id)) {
              baseItems[categoryKey].push({
                id: savedItem.id,
                name: savedItem.name,
                category: categoryKey,
                essential: savedItem.essential,
                quantity: savedItem.quantity,
                packed: false
              });
            }
          });
        }
      });
    }

    // Add gender-specific items
    if (userPreferences?.gender === 'female') {
      baseItems.toiletries.push(
        { id: 't_fem1', name: 'Feminine hygiene products', category: 'toiletries', essential: true, weight: 100, quantity: 1, packed: false },
        { id: 't_fem2', name: 'Makeup', category: 'toiletries', essential: false, weight: 200, quantity: 1, packed: false },
        { id: 't_fem3', name: 'Makeup remover', category: 'toiletries', essential: false, weight: 100, quantity: 1, packed: false },
        { id: 't_fem4', name: 'Hair ties/accessories', category: 'toiletries', essential: false, weight: 20, quantity: 1, packed: false }
      );
      baseItems.accessories.push(
        { id: 'ac_fem1', name: 'Hair accessories', category: 'accessories', essential: false, weight: 30, quantity: 1, packed: false }
      );
    }

    // Add weather-appropriate clothing items based on ACTUAL temperatures
    if (effectiveWeather) {
      // Very hot weather clothing (30°C+)
      if (effectiveWeather.veryHot) {
        const isBeachDest = effectiveWeather.beach_suitable;
        baseItems.clothing.push(
          { id: 'c_vhot1', name: 'Shorts', category: 'clothing', essential: true, weight: 200, quantity: getClothingQuantity(Math.max(3, tripLength / 3), adjustments.casual), packed: false },
          { id: 'c_vhot2', name: 'Light dresses', category: 'clothing', essential: false, weight: 300, quantity: getClothingQuantity(Math.max(2, tripLength / 4), adjustments.casual), packed: false },
          { id: 'c_vhot3', name: 'Tank tops', category: 'clothing', essential: false, weight: 120, quantity: getClothingQuantity(Math.max(3, tripLength / 3), adjustments.casual), packed: false },
          { id: 'c_vhot4', name: 'Light breathable shirts', category: 'clothing', essential: true, weight: 150, quantity: getClothingQuantity(Math.max(3, tripLength / 3), adjustments.casual), packed: false }
        );
      }
      
      // Hot weather clothing (24-29°C)
      if (effectiveWeather.hot) {
        const isBeachDest = effectiveWeather.beach_suitable;
        baseItems.clothing.push(
          { id: 'c_hot1', name: 'Shorts', category: 'clothing', essential: true, weight: 200, quantity: getClothingQuantity(Math.max(3, tripLength / 3), adjustments.casual), packed: false },
          { id: 'c_hot2', name: 'Light dresses', category: 'clothing', essential: false, weight: 300, quantity: getClothingQuantity(Math.max(2, tripLength / 4), adjustments.casual), packed: false },
          { id: 'c_hot3', name: 'Tank tops', category: 'clothing', essential: false, weight: 120, quantity: getClothingQuantity(Math.max(3, tripLength / 3), adjustments.casual), packed: false },
          { id: 'c_hot4', name: 'Light breathable shirts', category: 'clothing', essential: true, weight: 150, quantity: getClothingQuantity(Math.max(3, tripLength / 3), adjustments.casual), packed: false },
          { id: 'c_hot5', name: 'Short sleeve shirts', category: 'clothing', essential: true, weight: 150, quantity: getClothingQuantity(Math.max(3, tripLength / 3), adjustments.casual), packed: false },
          { id: 'c_hot6', name: 'Light cardigan', category: 'clothing', essential: false, weight: 250, quantity: 1, packed: false }
        );
      }
      
      // Warm weather clothing (18-23°C) - Transitional weather
      if (effectiveWeather.warm) {
        baseItems.clothing.push(
          { id: 'c_warm1', name: 'Light pants', category: 'clothing', essential: true, weight: 350, quantity: getClothingQuantity(Math.max(2, tripLength / 3), adjustments.casual), packed: false },
          { id: 'c_warm2', name: 'Long sleeve shirts', category: 'clothing', essential: false, weight: 200, quantity: getClothingQuantity(Math.max(2, tripLength / 4), adjustments.casual), packed: false },
          { id: 'c_warm3', name: 'Light sweater', category: 'clothing', essential: false, weight: 300, quantity: 1, packed: false },
          { id: 'c_warm4', name: 'Shorts (optional)', category: 'clothing', essential: false, weight: 200, quantity: 1, packed: false }
        );
      }

      // Beach-specific items (only for actual beach destinations with warm weather)
      if (effectiveWeather.beach_suitable && (effectiveWeather.veryHot || effectiveWeather.hot || effectiveWeather.warm)) {
        baseItems.clothing.push(
          { id: 'c_beach1', name: 'Swimwear', category: 'clothing', essential: true, weight: 100, quantity: Math.max(2, Math.ceil(tripLength / 5)), packed: false },
          { id: 'c_beach2', name: 'Beach cover up/Kaftan', category: 'clothing', essential: false, weight: 150, quantity: Math.max(1, Math.ceil(tripLength / 7)), packed: false },
          { id: 'c_beach3', name: 'Rash guard/Swim shirt', category: 'clothing', essential: false, weight: 150, quantity: 1, packed: false },
          { id: 'c_beach4', name: 'Beach shorts', category: 'clothing', essential: false, weight: 200, quantity: Math.max(2, Math.ceil(tripLength / 3)), packed: false },
          { id: 'c_beach5', name: 'Tank tops', category: 'clothing', essential: false, weight: 120, quantity: Math.max(2, Math.ceil(tripLength / 3)), packed: false },
          { id: 'c_beach6', name: 'Light summer dresses', category: 'clothing', essential: false, weight: 300, quantity: Math.max(1, Math.ceil(tripLength / 4)), packed: false },
          { id: 'c_beach7', name: 'Beach sarong/Wrap', category: 'clothing', essential: false, weight: 100, quantity: 1, packed: false },
          { id: 'c_beach8', name: 'Linen beach pants', category: 'clothing', essential: false, weight: 250, quantity: 1, packed: false },
          { id: 'c_beach9', name: 'Beach/swim dress', category: 'clothing', essential: false, weight: 200, quantity: 1, packed: false }
        );
        baseItems.accessories.push(
          { id: 'ac_beach1', name: 'Beach bag', category: 'accessories', essential: false, weight: 150, quantity: 1, packed: false },
          { id: 'ac_beach2', name: 'Beach towel', category: 'accessories', essential: false, weight: 500, quantity: Math.max(1, Math.ceil(tripLength / 7)), packed: false },
          { id: 'ac_beach3', name: 'Waterproof phone pouch', category: 'accessories', essential: false, weight: 50, quantity: 1, packed: false },
          { id: 'ac_beach4', name: 'Wide-brim sun hat', category: 'accessories', essential: false, weight: 120, quantity: 1, packed: false }
        );
      }

      // Desert-specific items
      if (effectiveWeather.destinationType === 'desert') {
        baseItems.clothing.push(
          { id: 'c_desert1', name: 'Long sleeve light shirts (sun protection)', category: 'clothing', essential: true, weight: 200, quantity: Math.max(2, Math.ceil(tripLength / 3)), packed: false },
          { id: 'c_desert2', name: 'Light pants (sun protection)', category: 'clothing', essential: true, weight: 300, quantity: Math.max(2, Math.ceil(tripLength / 3)), packed: false },
          { id: 'c_desert3', name: 'Light jacket (cool evenings)', category: 'clothing', essential: true, weight: 400, quantity: 1, packed: false }
        );
        baseItems.accessories.push(
          { id: 'ac_desert1', name: 'Desert scarf/head covering', category: 'accessories', essential: true, weight: 100, quantity: 1, packed: false }
        );
      }

      // Cool weather clothing (10-17°C)
      if (effectiveWeather.cool) {
        baseItems.clothing.push(
          { id: 'c_cool1', name: 'Long sleeve shirts/blouses', category: 'clothing', essential: true, weight: 250, quantity: getClothingQuantity(Math.max(3, tripLength / 2), adjustments.casual), packed: false },
          { id: 'c_cool2', name: 'Pants/Jeans', category: 'clothing', essential: true, weight: 500, quantity: getClothingQuantity(Math.max(3, tripLength / 2), adjustments.casual), packed: false },
          { id: 'c_cool3', name: 'Sweaters/Cardigans', category: 'clothing', essential: true, weight: 400, quantity: getClothingQuantity(Math.max(2, tripLength / 3), adjustments.casual), packed: false },
          { id: 'c_cool4', name: 'Jacket', category: 'clothing', essential: true, weight: 700, quantity: 1, packed: false }
        );
      }
      
      // Cold weather clothing (0-9°C)
      if (effectiveWeather.cold) {
        baseItems.clothing.push(
          { id: 'c_cold1', name: 'Long sleeve shirts/blouses', category: 'clothing', essential: true, weight: 250, quantity: getClothingQuantity(Math.max(3, tripLength / 2), adjustments.casual), packed: false },
          { id: 'c_cold2', name: 'Warm pants/Jeans', category: 'clothing', essential: true, weight: 500, quantity: getClothingQuantity(Math.max(3, tripLength / 2), adjustments.casual), packed: false },
          { id: 'c_cold3', name: 'Thick sweaters', category: 'clothing', essential: true, weight: 500, quantity: getClothingQuantity(Math.max(2, tripLength / 3), adjustments.casual), packed: false },
          { id: 'c_cold4', name: 'Warm winter jacket', category: 'clothing', essential: true, weight: 900, quantity: 1, packed: false },
          { id: 'c_cold6', name: 'Thermal underwear', category: 'clothing', essential: true, weight: 200, quantity: getClothingQuantity(Math.max(2, tripLength / 3), adjustments.basic), packed: false },
          { id: 'c_cold7', name: 'Thermal leggings/base layers', category: 'clothing', essential: true, weight: 180, quantity: getClothingQuantity(Math.max(2, tripLength / 3), adjustments.basic), packed: false }
        );
      }
      
      // Very cold weather clothing (Below 0°C)
      if (effectiveWeather.veryCold) {
        baseItems.clothing.push(
          { id: 'c_vcold1', name: 'Thermal underwear', category: 'clothing', essential: true, weight: 200, quantity: getClothingQuantity(Math.max(3, tripLength / 3), adjustments.basic), packed: false },
          { id: 'c_vcold2', name: 'Insulated pants', category: 'clothing', essential: true, weight: 600, quantity: getClothingQuantity(Math.max(2, tripLength / 3), adjustments.basic), packed: false },
          { id: 'c_vcold3', name: 'Heavy winter coat', category: 'clothing', essential: true, weight: 1200, quantity: 1, packed: false },
          { id: 'c_vcold5', name: 'Fleece layers', category: 'clothing', essential: true, weight: 400, quantity: 2, packed: false }
        );
      }

      // FOOTWEAR: Add weather and destination-appropriate shoes
      const shoes: PackingItem[] = [];
      
      // Very hot & beach destinations - prioritize sandals and flip flops
      if (effectiveWeather.veryHot || effectiveWeather.beach_suitable) {
        shoes.push(
          { id: 'shoe_flipflops', name: 'Flip flops/Beach sandals', category: 'clothing', essential: true, weight: 200, quantity: 1, packed: false },
          { id: 'shoe_sandals', name: 'Walking sandals', category: 'clothing', essential: true, weight: 350, quantity: 1, packed: false },
          { id: 'shoe_sneakers_light', name: 'Light sneakers/canvas shoes', category: 'clothing', essential: false, weight: 600, quantity: 1, packed: false }
        );
      }
      // Hot destinations - comfortable walking shoes + sandals (add flip flops for beach)
      else if (effectiveWeather.hot) {
        shoes.push(
          { id: 'shoe_sneakers', name: 'Comfortable walking shoes/sneakers', category: 'clothing', essential: true, weight: 700, quantity: 1, packed: false },
          { id: 'shoe_sandals', name: 'Sandals', category: 'clothing', essential: false, weight: 350, quantity: 1, packed: false }
        );
        // Add flip flops for beach destinations with hot weather
        if (effectiveWeather.beach_suitable) {
          shoes.push({ id: 'shoe_flipflops', name: 'Flip flops/Beach sandals', category: 'clothing', essential: true, weight: 200, quantity: 1, packed: false });
        }
      }
      // Warm & mild weather - versatile shoes (add flip flops for beach)
      else if (effectiveWeather.warm || effectiveWeather.mild) {
        shoes.push(
          { id: 'shoe_sneakers', name: 'Comfortable walking shoes/sneakers', category: 'clothing', essential: true, weight: 700, quantity: 1, packed: false },
          { id: 'shoe_casual', name: 'Casual shoes/loafers', category: 'clothing', essential: false, weight: 600, quantity: 1, packed: false }
        );
        // Add flip flops for beach destinations with warm weather
        if (effectiveWeather.beach_suitable) {
          shoes.push({ id: 'shoe_flipflops', name: 'Flip flops/Beach sandals', category: 'clothing', essential: true, weight: 200, quantity: 1, packed: false });
        }
      }
      // Cool weather - closed-toe comfortable shoes
      else if (effectiveWeather.cool) {
        shoes.push(
          { id: 'shoe_sneakers', name: 'Comfortable walking shoes/sneakers', category: 'clothing', essential: true, weight: 700, quantity: 1, packed: false },
          { id: 'shoe_boots_light', name: 'Light boots/ankle boots', category: 'clothing', essential: false, weight: 800, quantity: 1, packed: false }
        );
      }
      // Cold weather - warm boots
      else if (effectiveWeather.cold) {
        shoes.push(
          { id: 'shoe_boots_warm', name: 'Warm waterproof boots', category: 'clothing', essential: true, weight: 1000, quantity: 1, packed: false },
          { id: 'shoe_sneakers', name: 'Indoor shoes/sneakers', category: 'clothing', essential: false, weight: 700, quantity: 1, packed: false }
        );
      }
      // Very cold & snow - snow boots essential
      else if (effectiveWeather.veryCold || effectiveWeather.snow) {
        shoes.push(
          { id: 'shoe_snowboots', name: 'Insulated snow boots', category: 'clothing', essential: true, weight: 1200, quantity: 1, packed: false },
          { id: 'shoe_indoor', name: 'Indoor shoes/slippers', category: 'clothing', essential: true, weight: 300, quantity: 1, packed: false }
        );
      }
      // Default fallback - comfortable walking shoes
      else {
        shoes.push(
          { id: 'shoe_sneakers', name: 'Comfortable walking shoes/sneakers', category: 'clothing', essential: true, weight: 700, quantity: 1, packed: false }
        );
      }
      
      // Add waterproof shoes for rainy destinations
      if (effectiveWeather.rainy && !effectiveWeather.cold && !effectiveWeather.veryCold) {
        shoes.push(
          { id: 'shoe_waterproof', name: 'Waterproof shoes/rain boots', category: 'clothing', essential: true, weight: 800, quantity: 1, packed: false }
        );
      }
      
      // Add all shoes to clothing
      shoes.forEach(shoe => baseItems.clothing.push(shoe));
      
      // Optional shoes for all destinations
      baseItems.clothing.push(
        { id: 'shoe_dress', name: 'Dress shoes (for formal occasions)', category: 'clothing', essential: false, weight: 700, quantity: 1, packed: false },
        { id: 'shoe_slippers', name: 'Slippers/house shoes', category: 'clothing', essential: false, weight: 150, quantity: 1, packed: false }
      );

      // Universal clothing items for all weather types
      baseItems.clothing.push(
        { id: 'c_uni1', name: 'Skirts', category: 'clothing', essential: false, weight: 300, quantity: getClothingQuantity(Math.max(2, tripLength / 5), adjustments.casual), packed: false },
        { id: 'c_uni2', name: 'Dresses (day/evening)', category: 'clothing', essential: false, weight: 400, quantity: getClothingQuantity(Math.max(2, tripLength / 4), adjustments.casual), packed: false },
        { id: 'c_uni3', name: 'Loungewear', category: 'clothing', essential: false, weight: 200, quantity: getClothingQuantity(Math.max(2, tripLength / 5), adjustments.casual), packed: false },
        { id: 'c_uni4', name: 'Gym clothes', category: 'clothing', essential: false, weight: 180, quantity: getClothingQuantity(Math.max(2, tripLength / 5), adjustments.casual), packed: false },
        { id: 'c_uni5', name: 'Blazer - smart casual evenings', category: 'clothing', essential: false, weight: 500, quantity: 1, packed: false },
        { id: 'c_uni6', name: 'Formal clothes', category: 'clothing', essential: tripType.toLowerCase() === 'business' || tripType.toLowerCase() === 'romantic', weight: 600, quantity: getClothingQuantity(Math.max(1, tripLength / 7), adjustments.formal), packed: false }
      );
    }

    // Add weather-specific items (using effectiveWeather for safety)
    if (effectiveWeather) {
      const weatherItems: PackingItem[] = [];
      
      if (effectiveWeather.rainy) {
        weatherItems.push(
          { id: 'w1', name: 'Umbrella', category: 'accessories', essential: true, weight: 300, quantity: 1, packed: false },
          { id: 'w2', name: 'Raincoat', category: 'clothing', essential: true, weight: 400, quantity: 1, packed: false }
        );
      }
      
      if (effectiveWeather.cold || effectiveWeather.veryCold) {
        weatherItems.push(
          { id: 'w3', name: 'Warm gloves', category: 'accessories', essential: true, weight: 100, quantity: 1, packed: false },
          { id: 'w4', name: 'Scarf', category: 'accessories', essential: true, weight: 150, quantity: 1, packed: false },
          { id: 'w5', name: 'Warm hat/beanie', category: 'accessories', essential: true, weight: 80, quantity: 1, packed: false }
        );
      }
      
      if (effectiveWeather.sun_protection || effectiveWeather.hot || effectiveWeather.veryHot) {
        weatherItems.push(
          { id: 'w7', name: 'Sun hat', category: 'accessories', essential: false, weight: 100, quantity: 1, packed: false },
          { id: 'w8', name: 'Sunglasses', category: 'accessories', essential: false, weight: 50, quantity: 1, packed: false }
        );
        // Upgrade sunscreen to essential
        const sunscreenItem = baseItems.toiletries.find(item => item.name === 'Sunscreen');
        if (sunscreenItem) sunscreenItem.essential = true;
      }
      
      if (effectiveWeather.mosquitos) {
        weatherItems.push(
          { id: 'w9', name: 'Mosquito repellent', category: 'toiletries', essential: true, weight: 100, quantity: 1, packed: false },
          { id: 'w10', name: 'After bite/sting spray', category: 'toiletries', essential: false, weight: 80, quantity: 1, packed: false }
        );
      }
      
      if (effectiveWeather.snow) {
        weatherItems.push(
          { id: 'w12', name: 'Waterproof gloves', category: 'accessories', essential: true, weight: 150, quantity: 1, packed: false }
        );
      }
      
      // Add weather items to appropriate categories
      weatherItems.forEach(item => {
        if (baseItems[item.category]) {
          baseItems[item.category].push(item);
        } else {
          baseItems.misc.push(item);
        }
      });
    }

    // Add cultural and religious dress requirements
    const culturalRequirements = getCulturalDressRequirements();
    if (culturalRequirements.length > 0) {
      // Create cultural clothing category if we have requirements
      baseItems.cultural = [];
      
      culturalRequirements.forEach((requirement: any) => {
        // Add all cultural items
        requirement.items.forEach((item: PackingItem) => {
          const categoryItems = baseItems[item.category];
          const targetArray = Array.isArray(categoryItems) ? categoryItems : baseItems.misc;

          // Check if similar item already exists to avoid duplicates
          const exists = targetArray?.some(existing => {
            if (!existing || !existing.name) return false;
            const baseName = item.name?.split('(')[0]?.trim().toLowerCase();
            return baseName ? existing.name.toLowerCase().includes(baseName) : false;
          });
          
          if (!exists) {
            targetArray.push(item);
          }
        });
        
        // Add a note item to cultural category
        baseItems.cultural.push({
          id: `cult_note_${Math.random()}`,
          name: `📍 Cultural Note: ${requirement.note}`,
          category: 'cultural',
          essential: false,
          weight: 0,
          quantity: 1,
          packed: false
        });
      });
    }

    // Customize based on trip type with comprehensive default handling
    let customItems: PackingItem[] = [];
    
    console.log('🎯 Processing trip type:', tripType);
    
    switch (tripType.toLowerCase()) {
      case 'business':
        customItems = [
          { id: 'b1', name: 'Business suits', category: 'clothing', essential: true, weight: 800, quantity: Math.min(3, Math.max(1, Math.ceil(tripLength / 2))), packed: false },
          { id: 'b2', name: 'Blazer/Sport coat', category: 'clothing', essential: true, weight: 500, quantity: Math.max(1, Math.ceil(tripLength / 4)), packed: false },
          { id: 'b3', name: 'Dress shirts', category: 'clothing', essential: true, weight: 200, quantity: Math.max(3, Math.ceil(tripLength / 1.5)), packed: false },
          { id: 'b4', name: 'Formal trousers', category: 'clothing', essential: true, weight: 350, quantity: Math.max(2, Math.ceil(tripLength / 2)), packed: false },
          { id: 'b5', name: 'Business cards', category: 'documents', essential: true, weight: 20, quantity: 1, packed: false },
          { id: 'b6', name: 'Notebook', category: 'misc', essential: true, weight: 150, quantity: 1, packed: false },
          { id: 'b7', name: 'Pens', category: 'misc', essential: true, weight: 20, quantity: 3, packed: false },
        ];
        
        // Add gender-specific business items
        if (userPreferences?.gender === 'male') {
          customItems.push(
            { id: 'b_male1', name: 'Ties', category: 'clothing', essential: true, weight: 50, quantity: Math.max(2, Math.ceil(tripLength / 3)), packed: false },
            { id: 'b_male2', name: 'Dress shoes (formal)', category: 'clothing', essential: true, weight: 700, quantity: 1, packed: false }
          );
        } else if (userPreferences?.gender === 'female') {
          customItems.push(
            { id: 'b_fem1', name: 'Blouses', category: 'clothing', essential: true, weight: 200, quantity: Math.max(3, Math.ceil(tripLength / 1.5)), packed: false },
            { id: 'b_fem2', name: 'Business skirts/Dress pants', category: 'clothing', essential: true, weight: 350, quantity: Math.max(2, Math.ceil(tripLength / 2)), packed: false },
            { id: 'b_fem3', name: 'Professional dresses', category: 'clothing', essential: true, weight: 400, quantity: Math.max(1, Math.ceil(tripLength / 3)), packed: false },
            { id: 'b_fem4', name: 'Business heels/Flats', category: 'clothing', essential: true, weight: 600, quantity: 1, packed: false }
          );
        }
        
        // Make laptop and laptop charger essential for business trips
        const laptopItem = baseItems.electronics.find(item => item.name === 'Laptop');
        if (laptopItem) laptopItem.essential = true;
        const laptopChargerItem = baseItems.electronics.find(item => item.name === 'Laptop charger');
        if (laptopChargerItem) laptopChargerItem.essential = true;
        break;
      case 'adventure':
        customItems = [
          // Core hiking & trekking gear
          { id: 'a1', name: 'Hiking boots (broken in)', category: 'clothing', essential: true, weight: 1000, quantity: 1, packed: false },
          { id: 'a2', name: 'Trail running shoes', category: 'clothing', essential: false, weight: 600, quantity: 1, packed: false },
          { id: 'a3', name: 'Quick-dry shirts', category: 'clothing', essential: true, weight: 150, quantity: Math.max(3, Math.ceil(tripLength / 2)), packed: false },
          { id: 'a4', name: 'Hiking pants/convertible pants', category: 'clothing', essential: true, weight: 300, quantity: Math.max(2, Math.ceil(tripLength / 3)), packed: false },
          { id: 'a5', name: 'Moisture-wicking base layers', category: 'clothing', essential: true, weight: 180, quantity: 2, packed: false },
          { id: 'a6', name: 'Waterproof rain jacket', category: 'clothing', essential: true, weight: 400, quantity: 1, packed: false },
          { id: 'a7', name: 'Lightweight fleece/insulation layer', category: 'clothing', essential: true, weight: 350, quantity: 1, packed: false },
          { id: 'a8', name: 'Trekking poles', category: 'misc', essential: false, weight: 500, quantity: 2, packed: false },
          { id: 'a9', name: 'Daypack/hiking backpack (20-30L)', category: 'misc', essential: true, weight: 800, quantity: 1, packed: false },
          { id: 'a10', name: 'Hydration system/water bladder', category: 'misc', essential: true, weight: 200, quantity: 1, packed: false },
          
          // Climbing gear
          { id: 'a11', name: 'Climbing harness', category: 'misc', essential: false, weight: 400, quantity: 1, packed: false },
          { id: 'a12', name: 'Climbing helmet', category: 'misc', essential: false, weight: 350, quantity: 1, packed: false },
          { id: 'a13', name: 'Carabiners (locking & non-locking)', category: 'misc', essential: false, weight: 150, quantity: 5, packed: false },
          { id: 'a14', name: 'Climbing rope', category: 'misc', essential: false, weight: 2000, quantity: 1, packed: false },
          { id: 'a15', name: 'Belay device', category: 'misc', essential: false, weight: 100, quantity: 1, packed: false },
          { id: 'a16', name: 'Climbing chalk & chalk bag', category: 'misc', essential: false, weight: 150, quantity: 1, packed: false },
          { id: 'a17', name: 'Climbing gloves', category: 'accessories', essential: false, weight: 80, quantity: 1, packed: false },
          { id: 'a18', name: 'Quickdraws', category: 'misc', essential: false, weight: 100, quantity: 6, packed: false },
          
          // Water sports & diving gear
          { id: 'a19', name: 'Snorkeling mask & snorkel', category: 'misc', essential: false, weight: 400, quantity: 1, packed: false },
          { id: 'a20', name: 'Diving fins', category: 'misc', essential: false, weight: 800, quantity: 1, packed: false },
          { id: 'a21', name: 'Wetsuit/rash guard', category: 'clothing', essential: false, weight: 600, quantity: 1, packed: false },
          { id: 'a22', name: 'Dive computer/watch', category: 'electronics', essential: false, weight: 150, quantity: 1, packed: false },
          { id: 'a23', name: 'Waterproof camera/GoPro', category: 'electronics', essential: false, weight: 200, quantity: 1, packed: false },
          { id: 'a24', name: 'Dry bag (waterproof)', category: 'misc', essential: true, weight: 200, quantity: 2, packed: false },
          { id: 'a25', name: 'Water shoes/aqua socks', category: 'clothing', essential: false, weight: 300, quantity: 1, packed: false },
          
          // Camping gear
          { id: 'a26', name: 'Lightweight tent', category: 'misc', essential: false, weight: 2000, quantity: 1, packed: false },
          { id: 'a27', name: 'Sleeping bag', category: 'misc', essential: false, weight: 1000, quantity: 1, packed: false },
          { id: 'a28', name: 'Sleeping pad/mat', category: 'misc', essential: false, weight: 500, quantity: 1, packed: false },
          { id: 'a29', name: 'Camping stove & fuel', category: 'misc', essential: false, weight: 600, quantity: 1, packed: false },
          { id: 'a30', name: 'Cooking utensils/mess kit', category: 'misc', essential: false, weight: 300, quantity: 1, packed: false },
          
          // Safety & navigation
          { id: 'a31', name: 'Comprehensive first aid kit', category: 'misc', essential: true, weight: 400, quantity: 1, packed: false },
          { id: 'a32', name: 'Headlamp with extra batteries', category: 'electronics', essential: true, weight: 150, quantity: 1, packed: false },
          { id: 'a33', name: 'GPS device/offline maps', category: 'electronics', essential: true, weight: 200, quantity: 1, packed: false },
          { id: 'a34', name: 'Compass', category: 'misc', essential: true, weight: 50, quantity: 1, packed: false },
          { id: 'a35', name: 'Emergency whistle', category: 'misc', essential: true, weight: 20, quantity: 1, packed: false },
          { id: 'a36', name: 'Multi-tool/Swiss Army knife', category: 'misc', essential: true, weight: 150, quantity: 1, packed: false },
          { id: 'a37', name: 'Fire starter/waterproof matches', category: 'misc', essential: false, weight: 50, quantity: 1, packed: false },
          { id: 'a38', name: 'Emergency blanket', category: 'misc', essential: true, weight: 80, quantity: 1, packed: false },
          { id: 'a39', name: 'Signal mirror', category: 'misc', essential: false, weight: 30, quantity: 1, packed: false },
          
          // Sun & weather protection
          { id: 'a40', name: 'High SPF sunscreen', category: 'toiletries', essential: true, weight: 120, quantity: 1, packed: false },
          { id: 'a41', name: 'Lip balm with SPF', category: 'toiletries', essential: true, weight: 15, quantity: 1, packed: false },
          { id: 'a42', name: 'Sunglasses with UV protection', category: 'accessories', essential: true, weight: 50, quantity: 1, packed: false },
          { id: 'a43', name: 'Wide-brim hat/cap', category: 'accessories', essential: true, weight: 100, quantity: 1, packed: false },
          { id: 'a44', name: 'Buff/neck gaiter', category: 'accessories', essential: false, weight: 40, quantity: 1, packed: false },
          
          // Specialized items
          { id: 'a45', name: 'Action camera mounts & accessories', category: 'electronics', essential: false, weight: 200, quantity: 1, packed: false },
          { id: 'a46', name: 'Portable solar charger', category: 'electronics', essential: false, weight: 300, quantity: 1, packed: false },
          { id: 'a47', name: 'Paracord/rope (15m)', category: 'misc', essential: false, weight: 200, quantity: 1, packed: false },
          { id: 'a48', name: 'Duct tape (small roll)', category: 'misc', essential: false, weight: 100, quantity: 1, packed: false },
          { id: 'a49', name: 'Zip-lock bags (various sizes)', category: 'misc', essential: true, weight: 50, quantity: 10, packed: false },
          { id: 'a50', name: 'Insect repellent (strong)', category: 'toiletries', essential: true, weight: 100, quantity: 1, packed: false },
          { id: 'a51', name: 'Blister care kit', category: 'toiletries', essential: true, weight: 50, quantity: 1, packed: false },
          { id: 'a52', name: 'Energy bars/trail snacks', category: 'misc', essential: true, weight: 500, quantity: 10, packed: false },
          { id: 'a53', name: 'Water purification tablets', category: 'misc', essential: true, weight: 50, quantity: 1, packed: false },
          { id: 'a54', name: 'Lightweight towel (quick-dry)', category: 'accessories', essential: true, weight: 150, quantity: 1, packed: false },
          { id: 'a55', name: 'Gaiters (for snow/mud)', category: 'accessories', essential: false, weight: 150, quantity: 1, packed: false },
        ];
        break;
      case 'beach':
        // Only add beach items if destination is actually beach-suitable
        if (effectiveWeather?.beach_suitable) {
        customItems = [
          { id: 'be1', name: 'Swimwear', category: 'clothing', essential: true, weight: 100, quantity: Math.max(2, Math.ceil(tripLength / 5)), packed: false },
          { id: 'be2', name: 'Beach towel', category: 'misc', essential: false, weight: 500, quantity: Math.max(1, Math.ceil(tripLength / 7)), packed: false },
          { id: 'be3', name: 'Flip flops', category: 'clothing', essential: true, weight: 200, quantity: 1, packed: false },
          { id: 'be4', name: 'Sun hat', category: 'clothing', essential: false, weight: 100, quantity: 1, packed: false },
          { id: 'be5', name: 'Sunglasses', category: 'misc', essential: false, weight: 50, quantity: 1, packed: false },
          { id: 'be6', name: 'Beach bag', category: 'misc', essential: false, weight: 150, quantity: 1, packed: false },
          { id: 'be7', name: 'Waterproof phone case', category: 'electronics', essential: false, weight: 50, quantity: 1, packed: false },
        ];
        } else {
          // For non-beach destinations, suggest general leisure items instead
          customItems = [
            { id: 'be_alt1', name: 'Comfortable walking shoes', category: 'clothing', essential: true, weight: 600, quantity: 1, packed: false },
            { id: 'be_alt2', name: 'Daypack/backpack', category: 'misc', essential: true, weight: 300, quantity: 1, packed: false },
            { id: 'be_alt3', name: 'Comfortable casual clothes', category: 'clothing', essential: true, weight: 400, quantity: Math.max(3, Math.ceil(tripLength / 2)), packed: false },
            { id: 'be_alt4', name: 'Sunglasses', category: 'misc', essential: false, weight: 50, quantity: 1, packed: false },
          ];
        }
        break;
      case 'romantic':
        customItems = [
          { id: 'r1', name: 'Formal wear', category: 'clothing', essential: true, weight: 600, quantity: Math.max(2, Math.ceil(tripLength / 4)), packed: false },
          { id: 'r2', name: 'Nice perfume/cologne', category: 'toiletries', essential: false, weight: 100, quantity: 1, packed: false },
          { id: 'r3', name: 'Jewelry', category: 'misc', essential: false, weight: 50, quantity: 1, packed: false },
        ];
        break;
      case 'family':
        customItems = [
          { id: 'fam1', name: 'Comfortable family-friendly clothing', category: 'clothing', essential: true, weight: 300, quantity: Math.max(3, Math.ceil(tripLength / 2)), packed: false },
          { id: 'fam2', name: 'Snacks and entertainment for kids', category: 'misc', essential: true, weight: 400, quantity: Math.ceil(tripLength / 2), packed: false },
          { id: 'fam3', name: 'First aid kit (family-sized)', category: 'misc', essential: true, weight: 500, quantity: 1, packed: false },
          { id: 'fam4', name: 'Baby/child supplies (if applicable)', category: 'misc', essential: false, weight: 800, quantity: 1, packed: false },
          { id: 'fam5', name: 'Games and activities', category: 'misc', essential: false, weight: 300, quantity: 2, packed: false },
        ];
        break;
      case 'solo':
        customItems = [
          { id: 'sol1', name: 'Personal safety items', category: 'safety', essential: true, weight: 100, quantity: 1, packed: false },
          { id: 'sol2', name: 'Journal/notebook', category: 'misc', essential: false, weight: 150, quantity: 1, packed: false },
          { id: 'sol3', name: 'Entertainment (books, tablet)', category: 'misc', essential: false, weight: 400, quantity: 1, packed: false },
          { id: 'sol4', name: 'Portable door lock', category: 'safety', essential: false, weight: 80, quantity: 1, packed: false },
        ];
        break;
      case 'leisure':
      case 'city':
      case 'cultural':
      case 'sightseeing':
        // General leisure/city trip items
        customItems = [
          { id: 'leis1', name: 'Comfortable walking shoes', category: 'clothing', essential: true, weight: 700, quantity: 1, packed: false },
          { id: 'leis2', name: 'Daypack/backpack', category: 'misc', essential: true, weight: 400, quantity: 1, packed: false },
          { id: 'leis3', name: 'Camera', category: 'electronics', essential: false, weight: 500, quantity: 1, packed: false },
          { id: 'leis4', name: 'Travel guidebook/maps', category: 'misc', essential: false, weight: 200, quantity: 1, packed: false },
          { id: 'leis5', name: 'Reusable water bottle', category: 'misc', essential: true, weight: 200, quantity: 1, packed: false },
        ];
        break;
      default:
        // Comprehensive default for ANY trip type not explicitly handled
        console.log('⚠️ Trip type not explicitly handled, using comprehensive defaults:', tripType);
        customItems = [
          { id: 'def1', name: 'Comfortable walking shoes', category: 'clothing', essential: true, weight: 700, quantity: 1, packed: false },
          { id: 'def2', name: 'Casual day clothes', category: 'clothing', essential: true, weight: 350, quantity: Math.max(3, Math.ceil(tripLength / 2)), packed: false },
          { id: 'def3', name: 'Daypack/backpack', category: 'misc', essential: true, weight: 400, quantity: 1, packed: false },
          { id: 'def4', name: 'Reusable water bottle', category: 'misc', essential: true, weight: 200, quantity: 1, packed: false },
          { id: 'def5', name: 'Basic first aid kit', category: 'misc', essential: true, weight: 300, quantity: 1, packed: false },
          { id: 'def6', name: 'Travel adapter', category: 'electronics', essential: true, weight: 100, quantity: 1, packed: false },
        ];
        break;
    }
    
    console.log('✅ Generated', customItems.length, 'custom items for trip type:', tripType);

    // Add custom items to base categories
    console.log('📝 Adding', customItems.length, 'custom items to base categories');
    customItems.forEach(item => {
      if (baseItems[item.category]) {
        baseItems[item.category].push(item);
      } else {
        baseItems.misc.push(item);
      }
    });

    // Filter items based on clothing style preferences
    if (userPreferences?.clothing_styles && Array.isArray(userPreferences.clothing_styles) && userPreferences.clothing_styles.length > 0) {
      const clothingStyles = userPreferences.clothing_styles.map((s: string) => s.toLowerCase());
      
      // Remove items that don't match clothing style preferences
      baseItems.clothing = baseItems.clothing.filter(item => {
        const itemName = item.name.toLowerCase();
        
        // Always keep essential basics regardless of style
        if (item.name.match(/underwear|socks|sleepwear|pajamas|sneakers/i)) {
          return true;
        }
        
        // Casual style - exclude formal wear
        if (clothingStyles.includes('casual')) {
          if (itemName.includes('formal') || itemName.includes('suit') || itemName.includes('tie') || itemName.includes('blazer')) {
            return false;
          }
        }
        
        // Formal/Business style - exclude very casual items
        if (clothingStyles.includes('formal') || clothingStyles.includes('business')) {
          if (itemName.includes('flip flop') || itemName.includes('tank top') || itemName.includes('gym clothes')) {
            return false;
          }
        }
        
        // Athletic style - keep active wear
        if (clothingStyles.includes('athletic') || clothingStyles.includes('sporty')) {
          // Boost gym clothes and comfortable items
          if (itemName.includes('gym') || itemName.includes('athletic') || itemName.includes('sneakers')) {
            item.essential = true;
          }
        }
        
        // Minimalist style - reduce formal and dressy items
        if (clothingStyles.includes('minimalist')) {
          if (itemName.includes('jewelry') || itemName.includes('formal') || itemName.includes('dress shoes')) {
            return false;
          }
        }
        
        return true;
      });
    }

    // Apply luggage type restrictions
    const hasCarryOnOnly = luggageTypes.includes('carry-on') && !luggageTypes.includes('checked');
    const hasCheckedBag = luggageTypes.includes('checked');
    
    if (hasCarryOnOnly) {
      console.log('Applying carry-on only restrictions');
      
      // Aggressive reduction for carry-on only travelers
      Object.keys(baseItems).forEach(categoryKey => {
        baseItems[categoryKey] = baseItems[categoryKey].map(item => {
          // Keep essential single items as is
          if (item.essential && item.quantity === 1) {
            return item;
          }
          
          // Reduce quantities more aggressively
          let reductionFactor = 0.5; // 50% reduction
          
          // Even more aggressive for heavy/bulky items
          if (item.weight && item.weight > 400) {
            reductionFactor = 0.3; // 70% reduction for heavy items
          }
          
          // Category-specific reductions
          if (categoryKey === 'accessories') reductionFactor = 0.4;
          if (categoryKey === 'electronics') reductionFactor = 0.6;
          if (categoryKey === 'misc') reductionFactor = 0.5;
          
          return {
            ...item,
            quantity: Math.max(1, Math.floor(item.quantity * reductionFactor))
          };
        });
        
        // Remove non-essential heavy items for carry-on only
        baseItems[categoryKey] = baseItems[categoryKey].filter(item => {
          if (item.essential) return true;
          
          // Remove very heavy non-essential items
          if (item.weight && item.weight > 800) {
            console.log(`Removing heavy item for carry-on: ${item.name}`);
            return false;
          }
          
          // Remove bulky non-essentials
          const bulkyItems = ['boots', 'coat', 'beach towel', 'travel pillow', 'laptop'];
          if (bulkyItems.some(bulky => item.name.toLowerCase().includes(bulky))) {
            console.log(`Removing bulky item for carry-on: ${item.name}`);
            return false;
          }
          
          return true;
        });
      });
    }

    // Apply packing style adjustments based on user preferences
    const packingStyle = userPreferences?.packing_style || 'balanced';
    console.log('Applying packing style:', packingStyle);
    
    // Define multipliers for different packing styles
    const styleMultipliers: { [key: string]: { clothing: number; accessories: number; toiletries: number; electronics: number; misc: number; makeEssential: boolean } } = {
      'minimalist': {
        clothing: 0.5,        // 50% fewer clothing items - pack light
        accessories: 0.4,     // 60% fewer accessories - essentials only
        toiletries: 0.6,      // 40% fewer toiletries - basics only
        electronics: 0.7,     // 30% fewer electronics
        misc: 0.5,            // 50% fewer misc items
        makeEssential: false  // Don't change essential status
      },
      'balanced': {
        clothing: 1.0,        // Standard quantities
        accessories: 1.0,
        toiletries: 1.0,
        electronics: 1.0,
        misc: 1.0,
        makeEssential: false
      },
      'prepared': {          // Alias for balanced (for backward compatibility)
        clothing: 1.0,        
        accessories: 1.0,
        toiletries: 1.0,
        electronics: 1.0,
        misc: 1.0,
        makeEssential: false
      },
      'overpacker': {
        clothing: 1.6,        // 60% more clothing items - extra outfits
        accessories: 2.0,     // Double accessories - bring all options
        toiletries: 1.4,      // 40% more toiletries
        electronics: 1.5,     // 50% more electronics
        misc: 1.8,            // 80% more misc items
        makeEssential: false  // Don't change essential status
      },
      'just-in-case': {
        clothing: 2.0,        // Double clothing items - every scenario
        accessories: 2.5,     // 150% more accessories - all possibilities
        toiletries: 1.7,      // 70% more toiletries
        electronics: 1.8,     // 80% more electronics
        misc: 2.0,            // Double misc items
        makeEssential: true   // Make more items essential
      }
    };
    
    const multiplier = styleMultipliers[packingStyle] || styleMultipliers['balanced'];
    
    // Apply multipliers to all categories
    Object.keys(baseItems).forEach(categoryKey => {
      baseItems[categoryKey] = baseItems[categoryKey].map(item => {
        // Don't adjust quantities for essential documents or single-quantity items like passport, phone, etc.
        const isAbsolutelyEssential = 
          categoryKey === 'documents' || 
          item.name.toLowerCase().includes('passport') ||
          item.name.toLowerCase().includes('phone') ||
          item.name.toLowerCase().includes('wallet') ||
          item.name.toLowerCase().includes('credit card') ||
          item.name.toLowerCase().includes('debit card') ||
          item.name.toLowerCase().includes('cash') ||
          item.name.toLowerCase().includes('id') ||
          (item.quantity === 1 && item.essential);
        
        if (isAbsolutelyEssential && item.quantity === 1) {
          return item;
        }
        
        // Get the appropriate multiplier for this category
        let categoryMultiplier = 1.0;
        // Don't apply multipliers to cultural, safety, or documents categories - they're essential
        if (categoryKey === 'cultural' || categoryKey === 'safety' || categoryKey === 'documents') {
          categoryMultiplier = 1.0;
        } else if (categoryKey === 'clothing') categoryMultiplier = multiplier.clothing;
        else if (categoryKey === 'accessories') categoryMultiplier = multiplier.accessories;
        else if (categoryKey === 'toiletries') categoryMultiplier = multiplier.toiletries;
        else if (categoryKey === 'electronics') categoryMultiplier = multiplier.electronics;
        else if (categoryKey === 'misc') categoryMultiplier = multiplier.misc;
        
        // Calculate new quantity (at least 1, rounded to nearest integer)
        const newQuantity = Math.max(1, Math.round((item.quantity || 1) * categoryMultiplier));
        
        // For "just-in-case" packers, make some non-essential items essential
        const shouldMakeEssential = 
          multiplier.makeEssential && 
          !item.essential && 
          (categoryKey === 'clothing' || categoryKey === 'accessories') &&
          Math.random() > 0.7; // 30% of non-essential items become essential
        
        return {
          ...item,
          quantity: newQuantity,
          essential: item.essential || shouldMakeEssential
        };
      });
    });
    
    console.log('Packing style adjustments applied:', {
      style: packingStyle,
      multipliers: multiplier,
      sampleClothingCount: baseItems.clothing.reduce((sum, item) => sum + (item.quantity || 1), 0)
    });

    // Log final item counts for debugging
    console.log('📊 Final item counts by category:', Object.keys(baseItems).map(key => ({
      category: key,
      itemCount: baseItems[key].length
    })));

    // Create categories with icons
    const newCategories: PackingCategory[] = [
      {
        id: 'clothing',
        name: 'Clothing',
        icon: <Shirt className="h-4 w-4" />,
        color: 'bg-cyan-500',
        items: baseItems.clothing
      },
      {
        id: 'toiletries',
        name: 'Toiletries & Health',
        icon: <Heart className="h-4 w-4" />,
        color: 'bg-green-500',
        items: baseItems.toiletries
      },
      {
        id: 'accessories',
        name: 'Accessories',
        icon: <Gem className="h-4 w-4" />,
        color: 'bg-cyan-500',
        items: baseItems.accessories
      },
      {
        id: 'electronics',
        name: 'Electronics',
        icon: <Zap className="h-4 w-4" />,
        color: 'bg-yellow-500',
        items: baseItems.electronics
      },
      {
        id: 'documents',
        name: 'Documents & Money',
        icon: <FileText className="h-4 w-4" />,
        color: 'bg-red-500',
        items: baseItems.documents
      },
      {
        id: 'misc',
        name: 'Miscellaneous',
        icon: <Package className="h-4 w-4" />,
        color: 'bg-cyan-500',
        items: baseItems.misc
      }
    ];

    // Add safety category if high-risk destination
    if (baseItems.safety && baseItems.safety.length > 0) {
      newCategories.push({
        id: 'safety',
        name: 'Safety & Security',
        icon: <Shield className="h-4 w-4" />,
        color: 'bg-orange-500',
        items: baseItems.safety
      });
    }

    // Add cultural category if there are cultural requirements
    if (baseItems.cultural && baseItems.cultural.length > 0) {
      newCategories.push({
        id: 'cultural',
        name: 'Cultural & Religious',
        icon: <Landmark className="h-4 w-4" />,
        color: 'bg-cyan-500',
        items: baseItems.cultural
      });
    }

    // Weight optimization based on luggage configuration
    const optimizeForWeight = () => {
      // Calculate available weight in grams
      let availableWeightGrams = 0;
      const kgToGrams = 1000;
      const lbsToGrams = 453.592;
      
      const hasCarryOn = luggageTypes.includes('carry-on');
      const hasChecked = luggageTypes.includes('checked');
      const hasPersonal = luggageTypes.includes('personal');
      
      // Calculate total available weight
      if (hasCarryOn && carryOnLimit > 0) {
        const carryOnGrams = weightUnit === 'kg' ? carryOnLimit * kgToGrams : carryOnLimit * lbsToGrams;
        availableWeightGrams += carryOnGrams;
      }
      if (hasChecked && checkedLimit > 0) {
        const checkedGrams = weightUnit === 'kg' ? checkedLimit * kgToGrams : checkedLimit * lbsToGrams;
        availableWeightGrams += checkedGrams;
      }
      if (hasPersonal) {
        // Personal item typically allows ~5kg / 11lbs
        availableWeightGrams += 5 * kgToGrams;
      }
      
      // If no weight limits set, skip optimization
      if (availableWeightGrams === 0) {
        console.log('⚖️ No weight limits set, skipping weight optimization');
        return;
      }
      
      // Calculate current total weight
      const calculateTotalWeight = () => {
        let total = 0;
        newCategories.forEach(category => {
          category.items.forEach(item => {
            const itemWeight = (item.weight || 100) * (item.quantity || 1);
            total += itemWeight;
          });
        });
        return total;
      };
      
      let currentWeight = calculateTotalWeight();
      console.log(`⚖️ Weight optimization: Current ${(currentWeight / 1000).toFixed(2)}kg, Available ${(availableWeightGrams / 1000).toFixed(2)}kg`);
      
      // If within limits, no optimization needed
      if (currentWeight <= availableWeightGrams) {
        console.log('✅ Items fit within weight allowance');
        return;
      }
      
      // Need to reduce weight - calculate how much
      const excessWeight = currentWeight - availableWeightGrams;
      console.log(`⚠️ Over weight limit by ${(excessWeight / 1000).toFixed(2)}kg - optimizing quantities`);
      
      // Get all items sorted by priority (non-essential first, then by weight per item)
      const allItemsWithCategory: Array<{ item: PackingItem; categoryIndex: number; itemIndex: number; weightPerUnit: number }> = [];
      
      newCategories.forEach((category, catIdx) => {
        category.items.forEach((item, itemIdx) => {
          allItemsWithCategory.push({
            item,
            categoryIndex: catIdx,
            itemIndex: itemIdx,
            weightPerUnit: item.weight || 100
          });
        });
      });
      
      // Sort: non-essential items first (higher priority to reduce), then by weight (heavier first)
      allItemsWithCategory.sort((a, b) => {
        // Essential items last (we want to reduce non-essentials first)
        if (a.item.essential !== b.item.essential) {
          return a.item.essential ? 1 : -1;
        }
        // Within same essential status, reduce heavier items first
        return b.weightPerUnit - a.weightPerUnit;
      });
      
      let weightSaved = 0;
      const targetReduction = excessWeight;
      
      // Reduce quantities
      for (const { item, categoryIndex, itemIndex, weightPerUnit } of allItemsWithCategory) {
        if (weightSaved >= targetReduction) break;
        
        // Skip items that are already at quantity 1 and essential
        if ((item.quantity || 1) <= 1 && item.essential) continue;
        
        // For essential items at quantity 1, don't reduce
        if ((item.quantity || 1) === 1) continue;
        
        // Calculate how many we can reduce
        const currentQty = item.quantity || 1;
        const minQty = item.essential ? Math.max(1, Math.ceil(currentQty * 0.5)) : 1;
        const maxReduction = currentQty - minQty;
        
        if (maxReduction <= 0) continue;
        
        // How many units do we need to remove to meet target?
        const neededReduction = Math.ceil((targetReduction - weightSaved) / weightPerUnit);
        const actualReduction = Math.min(maxReduction, neededReduction);
        
        if (actualReduction > 0) {
          const newQty = currentQty - actualReduction;
          newCategories[categoryIndex].items[itemIndex] = {
            ...item,
            quantity: newQty
          };
          weightSaved += actualReduction * weightPerUnit;
          console.log(`📉 Reduced "${item.name}" from ${currentQty} to ${newQty} (saved ${(actualReduction * weightPerUnit / 1000).toFixed(2)}kg)`);
        }
      }
      
      const finalWeight = calculateTotalWeight();
      console.log(`⚖️ Final weight: ${(finalWeight / 1000).toFixed(2)}kg (saved ${(weightSaved / 1000).toFixed(2)}kg)`);
      
      if (finalWeight > availableWeightGrams) {
        console.warn(`⚠️ Still ${((finalWeight - availableWeightGrams) / 1000).toFixed(2)}kg over limit - essential items prevent further reduction`);
      }
    };
    
    // Apply weight optimization
    optimizeForWeight();

    setCategories(newCategories);
    // Preserve user-expanded state; only set defaults on first generation
    setExpandedCategories(prev => prev && prev.size > 0 ? prev : (isMobile ? new Set() : new Set(['clothing', 'toiletries'])));
    
    // Notify parent about items change
    if (onItemsChange) {
      const allItems = Object.values(baseItems).flat();
      onItemsChange(allItems);
    }
  };

  const toggleCategory = (categoryId: string, open?: boolean) => {
    const newExpanded = new Set(expandedCategories);
    if (open !== undefined) {
      if (open) {
        newExpanded.add(categoryId);
      } else {
        newExpanded.delete(categoryId);
      }
    } else {
      if (newExpanded.has(categoryId)) {
        newExpanded.delete(categoryId);
      } else {
        newExpanded.add(categoryId);
      }
    }
    console.log('[PackingList] toggleCategory', { categoryId, open, expanded: Array.from(newExpanded) });
    setExpandedCategories(newExpanded);
  };

  const toggleItemPacked = (categoryId: string, itemId: string) => {
    setCategories(prev => prev.map(category => {
      if (category.id === categoryId) {
        return {
          ...category,
          items: category.items.map(item => 
            item.id === itemId ? { ...item, packed: !item.packed } : item
          )
        };
      }
      return category;
    }));
  };

  const getCategoryProgress = (category: PackingCategory) => {
    const totalItems = category.items.length;
    const packedItems = category.items.filter(item => item.packed).length;
    return totalItems > 0 ? (packedItems / totalItems) * 100 : 0;
  };

  const getOverallProgress = () => {
    const totalItems = categories.reduce((sum, cat) => sum + cat.items.length, 0);
    const packedItems = categories.reduce((sum, cat) => sum + cat.items.filter(item => item.packed).length, 0);
    return totalItems > 0 ? (packedItems / totalItems) * 100 : 0;
  };

  const getEssentialProgress = () => {
    const essentialItems = categories.reduce((sum, cat) => sum + cat.items.filter(item => item.essential).length, 0);
    const packedEssentialItems = categories.reduce((sum, cat) => sum + cat.items.filter(item => item.essential && item.packed).length, 0);
    return essentialItems > 0 ? (packedEssentialItems / essentialItems) * 100 : 0;
  };

  const overallProgress = getOverallProgress();
  const essentialProgress = getEssentialProgress();

  useEffect(() => {
    if (essentialProgress === 100 && !isCompleted) {
      setIsCompleted(true);
    }
  }, [essentialProgress, isCompleted]);

  // Call onComplete when packing is finished - trigger celebration instead of direct animation
  // Only trigger once using celebrationShown flag to prevent re-triggering when modals are opened/closed
  useEffect(() => {
    if (isCompleted && !showLuggageAnimation && !celebrationShown) {
      // Small delay to let the user see the 100% progress
      const timer = setTimeout(() => {
        setShowCelebration(true);
        setCelebrationShown(true);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [isCompleted, showLuggageAnimation, celebrationShown]);

  const handleAnimationComplete = () => {
    setShowLuggageAnimation(false);
    onComplete();
  };

  const handleCelebrationContinue = () => {
    setShowCelebration(false);
    setShowLuggageAnimation(true);
  };

  const handleShareFromCelebration = () => {
    setShowCelebration(false);
    setShowShareCard(true);
  };

  const handleInviteFromCelebration = () => {
    setShowCelebration(false);
    setShowCollaborativeInvite(true);
  };

  const handleFinalizeTrip = () => {
    setShowCelebration(false);
    setShowLuggageAnimation(true);
    // Call onComplete to finalize the trip
    onComplete();
  };

  // Visa check timer - show modal after 10-15 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowVisaModal(true);
    }, 12000); // 12 seconds

    return () => clearTimeout(timer);
  }, []); // Only run once when component mounts

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Luggage Completion Animation */}
      {showLuggageAnimation && (
        <LuggageCompletionAnimation onComplete={handleAnimationComplete} />
      )}
      
      {/* Progress Overview */}
      <Card className="border-cyan-500/20 shadow-xl shadow-cyan-500/5 overflow-hidden">
        <div className="bg-gradient-to-br from-cyan-50 via-teal-50/50 to-transparent dark:from-cyan-950/30 dark:via-teal-950/20 dark:to-transparent px-4 sm:px-6 pt-5 pb-4 border-b border-border/40">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-1">
            <div className="flex items-center gap-3 min-w-0">
              <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-cyan-400 to-teal-500 text-white shadow-lg shadow-cyan-500/30">
                <CheckCircle2 className="h-5 w-5" />
              </span>
              <div className="min-w-0">
                <h3 className="text-lg sm:text-xl font-bold leading-tight">Packing Progress</h3>
                <p className="text-xs sm:text-sm text-muted-foreground">
                  Track what's done and what to grab next
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Badge className={cn(
                "w-fit border-0 font-semibold",
                essentialProgress === 100
                  ? "bg-gradient-to-r from-cyan-500 to-teal-500 text-white shadow-md shadow-cyan-500/20"
                  : "bg-cyan-500/15 text-cyan-700 dark:text-cyan-300 hover:bg-cyan-500/15"
              )}>
                {Math.round(essentialProgress)}% essentials
              </Badge>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowAddItemDialog(true)}
                className="gap-2 border-cyan-500/30 hover:bg-cyan-500/10 hover:text-cyan-700 dark:hover:text-cyan-300"
              >
                <Plus className="h-4 w-4" />
                Add Item
              </Button>
            </div>
          </div>
        </div>
        <CardContent className="space-y-4 pt-5">
          <div className="space-y-2">
            <div className="flex justify-between text-sm font-medium">
              <span>Essential items</span>
              <span className="text-cyan-600 dark:text-cyan-400 font-semibold">{Math.round(essentialProgress)}%</span>
            </div>
            <div className="relative h-2 rounded-full bg-muted overflow-hidden">
              <div
                className="absolute inset-y-0 left-0 bg-gradient-to-r from-cyan-400 to-teal-500 rounded-full transition-all duration-500"
                style={{ width: `${essentialProgress}%` }}
              />
            </div>
          </div>
          
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">All items</span>
              <span className="text-muted-foreground font-medium">{Math.round(overallProgress)}%</span>
            </div>
            <div className="relative h-1.5 rounded-full bg-muted overflow-hidden">
              <div
                className="absolute inset-y-0 left-0 bg-gradient-to-r from-cyan-400/70 to-teal-500/70 rounded-full transition-all duration-500"
                style={{ width: `${overallProgress}%` }}
              />
            </div>
          </div>

          {isCompleted && (
            <div className="flex flex-col gap-3 p-3 sm:p-4 bg-primary/10 rounded-lg border border-primary/20">
              <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-2 sm:justify-between">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0" />
                  <span className="font-medium text-primary">All essentials packed!</span>
                </div>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => setShowShareCard(true)}
                    className="gap-2"
                  >
                    <Sparkles className="h-4 w-4" />
                    Share Card
                  </Button>
                  <Button onClick={onComplete} size="sm" className="w-full sm:w-auto">
                    I'm Ready to Go!
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Offline Status Indicator */}
          {hasFeature('offline_mode') && isOffline && (
            <div className="flex items-center gap-2 p-3 bg-yellow-500/10 text-yellow-700 dark:text-yellow-400 rounded-lg border border-yellow-500/20">
              <CloudRain className="h-4 w-4" />
              <span className="text-sm font-medium">Offline Mode - List saved locally</span>
            </div>
          )}

          {/* Quick Actions */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setShowVisaModal(true)}
              className="w-full border-cyan-500/20 hover:bg-cyan-500/10 hover:text-cyan-700 dark:hover:text-cyan-300 hover:border-cyan-500/40"
            >
              <MapPin className="h-4 w-4 mr-2" />
              Visa Check
            </Button>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setShowShareCard(true)}
              className="w-full border-cyan-500/20 hover:bg-cyan-500/10 hover:text-cyan-700 dark:hover:text-cyan-300 hover:border-cyan-500/40"
            >
              <Sparkles className="h-4 w-4 mr-2" />
              Share Card
            </Button>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setShowCollaborativeInvite(true)}
              className="w-full border-cyan-500/20 hover:bg-cyan-500/10 hover:text-cyan-700 dark:hover:text-cyan-300 hover:border-cyan-500/40"
            >
              <Users className="h-4 w-4 mr-2" />
              Invite Friends
            </Button>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => {
                if (canEditPackingList) {
                  setShowAddItemDialog(true);
                } else {
                  toast.info("Upgrade to customize your packing list", {
                    description: "Add custom items with Premium Economy or First Class Pack",
                    action: {
                      label: "Upgrade",
                      onClick: () => window.location.href = "/pricing"
                    }
                  });
                }
              }}
              className="w-full border-cyan-500/20 hover:bg-cyan-500/10 hover:text-cyan-700 dark:hover:text-cyan-300 hover:border-cyan-500/40"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Item
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Template Manager - First Class Feature */}
      {hasFeature('custom_templates') && (
        <TemplateManager 
          currentCategories={categories}
          currentTripType={tripType}
          onLoadTemplate={handleLoadTemplate}
        />
      )}

      {/* Packing Categories */}
      <div className="space-y-3 sm:space-y-4">
        {categories.map(category => {
          const categoryProgress = getCategoryProgress(category);
          const essentialItems = category.items.filter(item => item.essential);
          const optionalItems = category.items.filter(item => !item.essential);
          const isExpanded = expandedCategories.has(category.id);

          return (
            <Card key={category.id} className="border-border/60 hover:border-cyan-500/30 hover:shadow-md hover:shadow-cyan-500/5 transition-all overflow-hidden">
              <Collapsible open={isExpanded} onOpenChange={(open) => toggleCategory(category.id, open)}>
                <CardHeader
                  role="button"
                  tabIndex={0}
                  aria-expanded={isExpanded}
                  onClick={() => toggleCategory(category.id, !isExpanded)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      toggleCategory(category.id, !isExpanded)
                    }
                  }}
                  className={cn(
                    "cursor-pointer transition-colors p-3 sm:p-5",
                    isExpanded
                      ? "bg-gradient-to-br from-cyan-50/60 to-transparent dark:from-cyan-950/15 dark:to-transparent"
                      : "hover:bg-muted/40"
                  )}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                      <div className={cn("p-2 rounded-lg text-white flex-shrink-0 shadow-sm", category.color)}>
                        {category.icon}
                      </div>
                      <div className="min-w-0 flex-1">
                        <CardTitle className="text-base sm:text-lg truncate">{category.name}</CardTitle>
                        <CardDescription className="text-xs sm:text-sm">
                          {category.items.filter(item => item.packed).length} of {category.items.length} packed
                        </CardDescription>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
                      <div className="text-right min-w-[60px] sm:min-w-[90px]">
                        <div className={cn(
                          "text-xs sm:text-sm font-semibold",
                          categoryProgress === 100 ? "text-cyan-600 dark:text-cyan-400" : "text-muted-foreground"
                        )}>{Math.round(categoryProgress)}%</div>
                        <div className="relative h-1.5 w-12 sm:w-20 rounded-full bg-muted overflow-hidden mt-1">
                          <div
                            className="absolute inset-y-0 left-0 bg-gradient-to-r from-cyan-400 to-teal-500 rounded-full transition-all duration-500"
                            style={{ width: `${categoryProgress}%` }}
                          />
                        </div>
                      </div>
                      {isExpanded ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
                    </div>
                  </div>
                </CardHeader>
                
                <CollapsibleContent>
                  <CardContent className="pt-0 px-3 sm:px-6 pb-3 sm:pb-6">
                    {/* Essential Items */}
                    {essentialItems.length > 0 && (
                      <div className="space-y-3 mb-4 sm:mb-6">
                        <div className="flex items-center gap-2">
                          <Badge className="text-xs bg-cyan-500/15 text-cyan-700 dark:text-cyan-300 border-cyan-500/30 hover:bg-cyan-500/15">Essential</Badge>
                          <span className="text-xs sm:text-sm text-muted-foreground">Don't forget these!</span>
                        </div>
                        <div className="space-y-2">
                          {essentialItems.map(item => (
                            <div 
                              key={item.id} 
                              className="group"
                            >
                              <div className={cn(
                                "flex items-center gap-3 p-3 sm:p-3 rounded-lg border transition-all touch-manipulation",
                                item.packed
                                  ? "border-cyan-500/20 bg-cyan-500/5"
                                  : "border-cyan-500/30 bg-gradient-to-br from-cyan-50/50 to-teal-50/30 dark:from-cyan-950/15 dark:to-teal-950/10 hover:border-cyan-500/50"
                              )}>
                                <Checkbox
                                  checked={item.packed}
                                  onCheckedChange={() => toggleItemPacked(category.id, item.id)}
                                  className="data-[state=checked]:bg-gradient-to-br data-[state=checked]:from-cyan-400 data-[state=checked]:to-teal-500 data-[state=checked]:border-transparent border-cyan-500/40 h-5 w-5 sm:h-4 sm:w-4"
                                />
                                <div className={cn("flex-1 min-w-0 transition-colors", item.packed && "text-muted-foreground")}>
                                  <div className={cn("font-medium text-sm sm:text-base break-words", item.packed && "line-through")}>
                                    {item.name}
                                  </div>
                                  {isItemEditing(item.id) ? (
                                    <div className="flex items-center gap-2 mt-1">
                                      <Label className="text-xs">Qty:</Label>
                                      <div className="flex items-center gap-1">
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          className="h-6 w-6 p-0"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            updateItemQuantity(category.id, item.id, Math.max(1, (item.quantity || 1) - 1));
                                          }}
                                        >
                                          <Minus className="h-3 w-3" />
                                        </Button>
                                        <span className="text-xs min-w-[20px] text-center">{item.quantity || 1}</span>
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          className="h-6 w-6 p-0"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            updateItemQuantity(category.id, item.id, (item.quantity || 1) + 1);
                                          }}
                                        >
                                          <Plus className="h-3 w-3" />
                                        </Button>
                                      </div>
                                    </div>
                                  ) : (
                                    item.quantity && item.quantity > 1 && (
                                      <div className="text-xs text-muted-foreground mt-0.5">
                                        Qty: {item.quantity}
                                      </div>
                                    )
                                  )}
                                </div>
                                <div className="flex-shrink-0 flex items-center gap-2">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-6 w-6 p-0 text-muted-foreground hover:text-foreground opacity-0 group-hover:opacity-100 transition-opacity"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (canEditPackingList) {
                                        toggleItemEdit(item.id);
                                      } else {
                                        toast.info("Upgrade to edit quantities", {
                                          description: "Customize your packing list with Premium Economy or First Class Pack",
                                          action: {
                                            label: "Upgrade",
                                            onClick: () => window.location.href = "/pricing"
                                          }
                                        });
                                      }
                                    }}
                                  >
                                    <Edit3 className="h-3 w-3" />
                                  </Button>
                                  {item.id.startsWith("custom_") && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive opacity-0 group-hover:opacity-100 transition-opacity"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        if (canEditPackingList) {
                                          removeItem(category.id, item.id);
                                        } else {
                                          toast.info("Upgrade to remove items", {
                                            description: "Customize your packing list with Premium Economy or First Class Pack",
                                            action: {
                                              label: "Upgrade",
                                              onClick: () => window.location.href = "/pricing"
                                            }
                                          });
                                        }
                                      }}
                                    >
                                      <Minus className="h-3 w-3" />
                                    </Button>
                                  )}
                                  {item.packed && <CheckCircle2 className="h-5 w-5 sm:h-4 sm:w-4 text-primary" />}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Optional Items */}
                    {optionalItems.length > 0 && (
                      <div className="space-y-3">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-xs border-border text-muted-foreground">Optional</Badge>
                          <span className="text-xs sm:text-sm text-muted-foreground">Nice to have</span>
                        </div>
                        <div className="space-y-2">
                          {optionalItems.map(item => (
                            <div 
                              key={item.id} 
                              className="group"
                            >
                              <div className={cn(
                                "flex items-center gap-3 p-3 rounded-lg border transition-all touch-manipulation",
                                item.packed
                                  ? "border-cyan-500/15 bg-cyan-500/5"
                                  : "border-border/50 hover:border-cyan-500/25 hover:bg-muted/30"
                              )}>
                                <Checkbox
                                  checked={item.packed}
                                  onCheckedChange={() => toggleItemPacked(category.id, item.id)}
                                  className="data-[state=checked]:bg-gradient-to-br data-[state=checked]:from-cyan-400 data-[state=checked]:to-teal-500 data-[state=checked]:border-transparent h-5 w-5 sm:h-4 sm:w-4"
                                />
                                <div className={cn("flex-1 min-w-0 transition-colors", item.packed && "text-muted-foreground")}>
                                  <div className={cn("font-medium text-sm sm:text-base break-words", item.packed && "line-through")}>
                                    {item.name}
                                  </div>
                                  {isItemEditing(item.id) ? (
                                    <div className="flex items-center gap-2 mt-1">
                                      <Label className="text-xs">Qty:</Label>
                                      <div className="flex items-center gap-1">
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          className="h-6 w-6 p-0"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            updateItemQuantity(category.id, item.id, Math.max(1, (item.quantity || 1) - 1));
                                          }}
                                        >
                                          <Minus className="h-3 w-3" />
                                        </Button>
                                        <span className="text-xs min-w-[20px] text-center">{item.quantity || 1}</span>
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          className="h-6 w-6 p-0"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            updateItemQuantity(category.id, item.id, (item.quantity || 1) + 1);
                                          }}
                                        >
                                          <Plus className="h-3 w-3" />
                                        </Button>
                                      </div>
                                    </div>
                                  ) : (
                                    item.quantity && item.quantity > 1 && (
                                      <div className="text-xs text-muted-foreground mt-0.5">
                                        Qty: {item.quantity}
                                      </div>
                                    )
                                  )}
                                </div>
                                <div className="flex-shrink-0 flex items-center gap-2">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-6 w-6 p-0 text-muted-foreground hover:text-foreground opacity-0 group-hover:opacity-100 transition-opacity"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (canEditPackingList) {
                                        toggleItemEdit(item.id);
                                      } else {
                                        toast.info("Upgrade to edit quantities", {
                                          description: "Customize your packing list with Premium Economy or First Class Pack",
                                          action: {
                                            label: "Upgrade",
                                            onClick: () => window.location.href = "/pricing"
                                          }
                                        });
                                      }
                                    }}
                                  >
                                    <Edit3 className="h-3 w-3" />
                                  </Button>
                                  {item.id.startsWith("custom_") && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive opacity-0 group-hover:opacity-100 transition-opacity"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        if (canEditPackingList) {
                                          removeItem(category.id, item.id);
                                        } else {
                                          toast.info("Upgrade to remove items", {
                                            description: "Customize your packing list with Premium Economy or First Class Pack",
                                            action: {
                                              label: "Upgrade",
                                              onClick: () => window.location.href = "/pricing"
                                            }
                                          });
                                        }
                                      }}
                                    >
                                      <Minus className="h-3 w-3" />
                                    </Button>
                                  )}
                                  {item.packed && <Circle className="h-5 w-5 sm:h-4 sm:w-4 text-muted-foreground" />}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </CollapsibleContent>
              </Collapsible>
            </Card>
          );
        })}
      </div>

      {/* Add Item Dialog */}
      <Dialog open={showAddItemDialog} onOpenChange={setShowAddItemDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add New Item</DialogTitle>
            <DialogDescription>
              Add a custom item to your packing list
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="item-name">Item Name</Label>
              <Input
                id="item-name"
                placeholder="Enter item name"
                value={newItemName}
                onChange={(e) => setNewItemName(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="category">Category</Label>
              <Select value={newItemCategory} onValueChange={setNewItemCategory}>
                <SelectTrigger>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="clothing">Clothing</SelectItem>
                  <SelectItem value="toiletries">Toiletries & Health</SelectItem>
                  <SelectItem value="electronics">Electronics</SelectItem>
                  <SelectItem value="documents">Documents & Money</SelectItem>
                  <SelectItem value="accessories">Accessories</SelectItem>
                  <SelectItem value="misc">Miscellaneous</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="quantity">Quantity</Label>
                <Input
                  id="quantity"
                  type="number"
                  min="1"
                  value={newItemQuantity}
                  onChange={(e) => setNewItemQuantity(parseInt(e.target.value) || 1)}
                />
              </div>
              <div className="space-y-2">
                <Label className="text-sm">Priority</Label>
                <div className="flex items-center space-x-2 pt-2">
                  <Checkbox
                    id="essential"
                    checked={newItemEssential}
                    onCheckedChange={(checked) => setNewItemEssential(checked as boolean)}
                  />
                  <Label htmlFor="essential" className="text-sm">Essential item</Label>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddItemDialog(false)}>
              Cancel
            </Button>
            <Button onClick={addNewItem} disabled={!newItemName.trim() || !newItemCategory}>
              Add Item
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Visa Check Modal */}
      <VisaCheckModal
        isOpen={showVisaModal}
        onClose={() => setShowVisaModal(false)}
        destination={destination}
        destinationCountry={country}
      />

      {/* Share Packing List Modal */}
      <SharePackingListModal
        open={showShareModal}
        onOpenChange={setShowShareModal}
        items={categories.flatMap(cat => cat.items)}
        destination={destination}
        tripType={tripType}
        tripLength={tripLength}
      />

      {/* Shareable Packing Card Modal */}
      <ShareablePackingCard
        open={showShareCard}
        onOpenChange={setShowShareCard}
        destination={destination}
        country={country}
        tripType={tripType}
        tripLength={tripLength}
        startDate={startDate}
        items={categories.flatMap(cat => cat.items)}
        packedPercentage={overallProgress}
      />

      {/* Trip Completed Celebration Modal */}
      <TripCompletedCelebration
        open={showCelebration}
        onOpenChange={setShowCelebration}
        destination={destination}
        tripLength={tripLength}
        tripType={tripType}
        packedItems={categories.reduce((sum, cat) => sum + cat.items.filter(item => item.packed).length, 0)}
        totalItems={categories.reduce((sum, cat) => sum + cat.items.length, 0)}
        onShareCard={handleShareFromCelebration}
        onInviteFriends={handleInviteFromCelebration}
        onContinue={handleCelebrationContinue}
        onFinalizeTrip={handleFinalizeTrip}
      />

      {/* Collaborative Packing Invite Modal */}
      <CollaborativePackingInvite
        open={showCollaborativeInvite}
        onOpenChange={setShowCollaborativeInvite}
        destination={destination}
        shareCode={shareCode}
      />
    </div>
  );
}